////////////////////////////////////////////////////////////////////////////////
//
//
// (C) Copyright 2023 Autodesk, Inc. All rights reserved.
//
//                      ****  CONFIDENTIAL MATERIAL  ****
//
// The information contained herein is confidential, proprietary to
// Autodesk, Inc., and considered a trade secret.  Use of this information
// by anyone other than authorized employees of Autodesk, Inc. is granted
// only under a written nondisclosure agreement, expressly prescribing the
// the scope and manner of such use.
//
////////////////////////////////////////////////////////////////////////////////
// noinspection UnnecessaryLocalVariableJS

//----------------------
// <auto-generated>
//     Generated using the NSwag toolchain v14.6.3.0 (NJsonSchema v11.5.2.0 (Newtonsoft.Json v13.0.0.0)) (http://NSwag.org)
// </auto-generated>
//----------------------

/* tslint:disable */
/* eslint-disable */
// ReSharper disable InconsistentNaming

import axios, {AxiosError} from 'axios';
import type {AxiosInstance, AxiosRequestConfig, AxiosResponse, CancelToken} from 'axios';
import {
  CreateCustomerRequest,
  CreateJobRequest,
  CreateOutgoingWebhookRequest,
  CreateProjectWiseConfigurationDto,
  CreateProjectWiseCredentialDto,
  CustomerInfo,
  DeckardAuthData,
  DetailedUsage,
  DownloadDto,
  EditCustomerRequest,
  EditJobRequest,
  EditOutgoingWebhookRequest,
  FileStatus,
  GDPRWebhook,
  GDPRWebhookResponse,
  GetRvtVersionResponse,
  HealthStatus,
  Hub,
  Job,
  JobRun,
  JobRunItemStatusType,
  JobRunStatusType,
  OutgoingWebhook,
  OutgoingWebhookBody,
  OutgoingWebhookEventPaginatedResult,
  OutgoingWebhookPaginatedResult,
  OutgoingWebhookSecret,
  OutgoingWebhookSendAttempt,
  ProblemDetails,
  Project,
  ProjectRevitVersionDetectionResult,
  ProjectWiseConfiguration,
  ProjectWiseConfigurationPaginatedResult,
  ProjectWiseCredential,
  ProjectWiseCredentialPaginatedResult,
  ProjectWiseCredentialWithSecret,
  StartExtractRequest,
  StartExtractResponse,
  StartProjectRevitVersionDetectionRequest,
  StartUsageDownloadResult,
  StorageType,
  UpdateProjectWiseConfigurationDto,
  UpdateProjectWiseCredentialDto,
  UsageExportFormat,
  UsageExportType,
  UsageSortType,
  UsageSummary,
  UserAccount,
  UserBase,
  WebhookBody,
  ZipEntryDto
} from "./Classes";

export interface IClient {
  /**
   * Callback for 3 legged authentication.
   * @param code (optional) The code returned by the authentication API.
   * @param callbackUrlOverride (optional) The callback URL, optionally.
   * @return An action result containing the authentication token.
   */
  callback(code?: string | undefined, callbackUrlOverride?: string | null | undefined, cancelToken?: CancelToken): Promise<DeckardAuthData>;

  /**
   * Endpoint to get a new access token using a refresh token.
   * @param userId The user id.
   * @param refreshToken The refresh token.
   * @return An action result containing the authentication token.
   */
  refreshToken(userId: string, refreshToken: string, cancelToken?: CancelToken): Promise<DeckardAuthData>;

  /**
   * Creates a job according to the information in the CreateJobCommand object.
   * @param createJobRequest (optional) The object defining the parameters of the job.
   * @return An object with information about the job.
   */
  createJob(createJobRequest?: CreateJobRequest | null | undefined, cancelToken?: CancelToken): Promise<Job>;

  /**
   * Gets all jobs for the current user.
   * @return An object with information about the job.
   */
  getJobs(cancelToken?: CancelToken): Promise<Job[]>;

  /**
   * Gets the job with the given ID.
   * @param id The job ID.
   * @return An object with information about the job.
   */
  deleteJob(id: string, cancelToken?: CancelToken): Promise<void>;

  /**
   * Edits a job according to the information in the EditJobDto object.
   * @param id The job ID.
   * @param dto (optional) The DTO with the updated information.
   * @return An object with information about the job.
   */
  editJob(id: string, dto?: EditJobRequest | null | undefined, cancelToken?: CancelToken): Promise<Job>;

  /**
   * Gets the job with the given ID.
   * @param id The job ID.
   * @param loadFilesAndFolders (optional) Whether to load the job files and folders. Defaults to true.
   * @param loadItems (optional) Whether to load the job items. Defaults to false.
   * @return An object with information about the job.
   */
  getJob(id: string, loadFilesAndFolders?: boolean | undefined, loadItems?: boolean | undefined, cancelToken?: CancelToken): Promise<Job>;

  /**
   * Gets a run for a job.
   * @param jobRunId The job run id.
   * @return An object with information about the job.
   */
  getJobRun(jobRunId: string, cancelToken?: CancelToken): Promise<JobRun>;

  /**
   * Performs a job callback.
   * @param accessKey (optional) The access key to ensure that this request is performed by the callback.
   * @param id (optional) The job id.
   * @param result (optional) The job result.
   * @return A result indicating the status code.
   */
  jobCallback(accessKey?: string | undefined, id?: string | null | undefined, result?: any | null | undefined, cancelToken?: CancelToken): Promise<void>;

  /**
   * Starts a job according to the information in the StartJobCommand object.
   * @param jobId The job id.
   * @return An object with information about the job.
   */
  startJob(jobId: string, cancelToken?: CancelToken): Promise<JobRun>;

  getCsvDownload(id?: string | undefined, cancelToken?: CancelToken): Promise<DownloadDto>;

  getDetailedUsage(statuses?: JobRunItemStatusType[] | undefined, limit?: number | null | undefined, paginationToken?: string | null | undefined, filter_CustomerIds?: string[] | undefined, filter_UserIds?: string[] | undefined, filter_JobIds?: string[] | undefined, filter_JobRunIds?: string[] | undefined, filter_StartTime?: Date | null | undefined, filter_EndTime?: Date | null | undefined, sort_SortType?: UsageSortType | undefined, sort_Ascending?: boolean | undefined, options_IncludeFullErrorInfo?: boolean | undefined, options_FileEntryLimit?: number | undefined, exportType?: UsageExportType | undefined, exportFormat?: UsageExportFormat | undefined, writeIfEmpty?: boolean | undefined, cancelToken?: CancelToken): Promise<DetailedUsage>;

  getUsageDownload(id?: string | undefined, cancelToken?: CancelToken): Promise<DownloadDto>;

  getUsageSummary(statuses?: JobRunStatusType[] | undefined, includeRuns?: boolean | undefined, limit?: number | null | undefined, paginationToken?: string | null | undefined, filter_CustomerIds?: string[] | undefined, filter_UserIds?: string[] | undefined, filter_JobIds?: string[] | undefined, filter_JobRunIds?: string[] | undefined, filter_StartTime?: Date | null | undefined, filter_EndTime?: Date | null | undefined, sort_SortType?: UsageSortType | undefined, sort_Ascending?: boolean | undefined, options_IncludeFullErrorInfo?: boolean | undefined, options_FileEntryLimit?: number | undefined, exportType?: UsageExportType | undefined, exportFormat?: UsageExportFormat | undefined, writeIfEmpty?: boolean | undefined, cancelToken?: CancelToken): Promise<UsageSummary>;

  startCsvDownload(filter_CustomerIds?: string[] | undefined, filter_UserIds?: string[] | undefined, filter_JobIds?: string[] | undefined, filter_JobRunIds?: string[] | undefined, filter_StartTime?: Date | null | undefined, filter_EndTime?: Date | null | undefined, sort_SortType?: UsageSortType | undefined, sort_Ascending?: boolean | undefined, options_IncludeFullErrorInfo?: boolean | undefined, options_FileEntryLimit?: number | undefined, exportType?: UsageExportType | undefined, exportFormat?: UsageExportFormat | undefined, writeIfEmpty?: boolean | undefined, cancelToken?: CancelToken): Promise<StartUsageDownloadResult>;

  startUsageDownload(filter_CustomerIds?: string[] | undefined, filter_UserIds?: string[] | undefined, filter_JobIds?: string[] | undefined, filter_JobRunIds?: string[] | undefined, filter_StartTime?: Date | null | undefined, filter_EndTime?: Date | null | undefined, sort_SortType?: UsageSortType | undefined, sort_Ascending?: boolean | undefined, options_IncludeFullErrorInfo?: boolean | undefined, options_FileEntryLimit?: number | undefined, exportType?: UsageExportType | undefined, exportFormat?: UsageExportFormat | undefined, writeIfEmpty?: boolean | undefined, cancelToken?: CancelToken): Promise<StartUsageDownloadResult>;

  /**
   * Creates a webhook according to the information in the DTO object.
   * @param createWebhookDto (optional) The object defining the parameters of the webhook.
   * @return An object with information about the webhook.
   */
  createWebhook(createWebhookDto?: CreateOutgoingWebhookRequest | null | undefined, cancelToken?: CancelToken): Promise<OutgoingWebhook>;

  /**
   * Gets all webhooks for the current user.
   * @param isDeleted (optional) Whether to include non-deleted, deleted or all webhooks.
   * @param paginationToken (optional) The pagination token, optionally.
   * @param limit (optional) The max number of items to return, optionally.
   * @return An object with information about the webhook.
   */
  listWebhooks(isDeleted?: boolean | null | undefined, paginationToken?: string | null | undefined, limit?: number | null | undefined, cancelToken?: CancelToken): Promise<OutgoingWebhookPaginatedResult>;

  /**
   * Deletes the webhook with the given ID.
   * @return A status code indicating the result.
   */
  deleteUserWebhooks(cancelToken?: CancelToken): Promise<void>;

  /**
   * Deletes the webhook with the given ID for the given user.
   * @param userId The user ID.
   * @return A status code indicating the result.
   */
  deleteUserWebhooksAdmin(userId: string, cancelToken?: CancelToken): Promise<void>;

  /**
   * Deletes the webhook with the given ID.
   * @param id The webhook ID.
   * @return A status code indicating the result.
   */
  deleteWebhook(id: string, cancelToken?: CancelToken): Promise<void>;

  /**
   * Edits a webhook according to the information in the DTO object.
   * @param id The webhook ID.
   * @param dto (optional) The DTO with the updated information.
   * @return An object with information about the webhook.
   */
  editWebhook(id: string, dto?: EditOutgoingWebhookRequest | null | undefined, cancelToken?: CancelToken): Promise<OutgoingWebhook>;

  /**
   * Gets the webhook with the given ID.
   * @param id The webhook ID.
   * @return An object with information about the webhook.
   */
  getWebhook(id: string, cancelToken?: CancelToken): Promise<OutgoingWebhook>;

  /**
   * Deletes the webhook with the given ID for the given user.
   * @param userId The user ID.
   * @param id The webhook ID.
   * @return A status code indicating the result.
   */
  deleteWebhookAdmin(userId: string, id: string, cancelToken?: CancelToken): Promise<void>;

  /**
   * Edits a webhook according to the information in the DTO object.
   * @param userId The user ID.
   * @param id The webhook ID.
   * @param dto (optional) The DTO with the updated information.
   * @return An object with information about the webhook.
   */
  editWebhookAdmin(userId: string, id: string, dto?: EditOutgoingWebhookRequest | null | undefined, cancelToken?: CancelToken): Promise<OutgoingWebhook>;

  /**
   * Gets the webhook with the given ID.
   * @param id The webhook ID.
   * @return An object with information about the webhook.
   */
  getWebhookSecret(id: string, cancelToken?: CancelToken): Promise<OutgoingWebhookSecret>;

  /**
   * Gets all webhooks for the current user.
   * @param isDeleted (optional) Whether to include non-deleted, deleted or all webhooks.
   * @param paginationToken (optional) The pagination token, optionally.
   * @param limit (optional) The max number of items to return, optionally.
   * @return An object with information about the webhook.
   */
  listUserWebhookHistories(isDeleted?: boolean | null | undefined, paginationToken?: string | null | undefined, limit?: number | null | undefined, cancelToken?: CancelToken): Promise<OutgoingWebhookEventPaginatedResult>;

  /**
   * Gets all history for a single webhook.
   * @param id The webhook ID.
   * @param paginationToken (optional) The pagination token, optionally.
   * @param limit (optional) The max number of items to return, optionally.
   * @return An object with information about the webhook.
   */
  listWebhookHistory(id: string, paginationToken?: string | null | undefined, limit?: number | null | undefined, cancelToken?: CancelToken): Promise<OutgoingWebhookEventPaginatedResult>;

  /**
   * Gets all webhooks for the current user.
   * @param userId The user ID for which to retrieve the webhooks.
   * @param isDeleted (optional) Whether to include non-deleted, deleted or all webhooks.
   * @param paginationToken (optional) The pagination token, optionally.
   * @param limit (optional) The max number of items to return, optionally.
   * @return An object with information about the webhook.
   */
  listWebhooksAdmin(userId: string, isDeleted?: boolean | null | undefined, paginationToken?: string | null | undefined, limit?: number | null | undefined, cancelToken?: CancelToken): Promise<OutgoingWebhookPaginatedResult>;

  /**
   * Previews a webhook according to the information in the DTO object.
   * @param id The webhook ID.
   * @param dto (optional) The DTO with the updated information, optionally.
   * @return An object with information about the webhook.
   */
  previewExistingWebhookBody(id: string, dto?: EditOutgoingWebhookRequest | null | undefined, cancelToken?: CancelToken): Promise<OutgoingWebhookBody>;

  /**
   * Previews a webhook according to the information in the DTO object.
   * @param createWebhookDto (optional) The object defining the parameters of the webhook.
   * @return An object with information about the webhook.
   */
  previewWebhookBody(createWebhookDto?: CreateOutgoingWebhookRequest | null | undefined, cancelToken?: CancelToken): Promise<OutgoingWebhookBody>;

  /**
   * Tests an existing webhook to preview its response. Optionally, include information in the edit DTO to preview the
   response before saving.
   * @param id The webhook ID.
   * @param dto (optional) The DTO with the updated information, optionally.
   * @return An object with information about the webhook.
   */
  testExistingWebhook(id: string, dto?: EditOutgoingWebhookRequest | null | undefined, cancelToken?: CancelToken): Promise<OutgoingWebhookSendAttempt>;

  /**
   * Tests a new webhook before it has been saved.
   * @param createWebhookDto (optional) The object defining the parameters of the webhook.
   * @return An object with information about the webhook.
   */
  testNewWebhook(createWebhookDto?: CreateOutgoingWebhookRequest | null | undefined, cancelToken?: CancelToken): Promise<OutgoingWebhookSendAttempt>;

  /**
   * Lists the contents of a ZIP file.
   * @param projectId The project id.
   * @param fileId The file id.
   * @param storageType (optional) The storage type. Defaults to Forge.
   * @param allowDeleted (optional) Whether to list a deleted file's contents.
   * @return The list of files contained in the ZIP.
   */
  listZipContents(projectId: string, fileId: string, storageType?: StorageType | undefined, allowDeleted?: boolean | undefined, cancelToken?: CancelToken): Promise<ZipEntryDto[]>;

  /**
   * Extracts the contents of a ZIP file.
   * @param extractRequest (optional) The parameters for the extraction job.
   * @return A list of status objects for the job.
   */
  startExtract(extractRequest?: StartExtractRequest | null | undefined, cancelToken?: CancelToken): Promise<StartExtractResponse[]>;

  /**
   * Gets the status of an extraction job.
   * @param id The job ID.
   * @return The current status of the job.
   */
  status(id: string, cancelToken?: CancelToken): Promise<FileStatus>;

  /**
   * Creates a customer.
   * @param dto (optional) A DTO containing the customer information.
   * @return The created customer.
   */
  createCustomer(dto?: CreateCustomerRequest | null | undefined, cancelToken?: CancelToken): Promise<CustomerInfo>;

  /**
   * Lists all customers.
   * @return All customers.
   * @deprecated
   */
  listCustomers(cancelToken?: CancelToken): Promise<CustomerInfo[]>;

  /**
   * Deletes a customer.
   * @param id The email domain (ID) of the customer.
   * @return A Task representing the asynchronous operation.
   */
  deleteCustomer(id: string, cancelToken?: CancelToken): Promise<void>;

  /**
   * Gets a customer by ID.
   * @param id The customer ID (email domain).
   * @return The customer, or null if none matches the ID.
   */
  getCustomer(id: string, cancelToken?: CancelToken): Promise<CustomerInfo>;

  /**
   * Edits a customer.
   * @param id The email domain (ID) of the customer.
   * @param dto (optional) A DTO containing the information to edit.
   * @return A Task representing the asynchronous operation.
   */
  updateCustomer(id: string, dto?: EditCustomerRequest | null | undefined, cancelToken?: CancelToken): Promise<void>;

  /**
   * Returns the application health status.
   * @return The application health status.
   */
  heartbeat(cancelToken?: CancelToken): Promise<HealthStatus>;

  getProject(hubId: string | null, projectId: string, storageType?: StorageType | null | undefined, cancelToken?: CancelToken): Promise<Project>;

  getProjectRvtVersionDetection(id: string, cancelToken?: CancelToken): Promise<ProjectRevitVersionDetectionResult>;

  getProjectRvtVersionNon(hubId: string | null, projectId: string, checkDeleted?: boolean | undefined, cancelToken?: CancelToken): Promise<GetRvtVersionResponse>;

  listAll(includeLegacyHubs?: boolean | undefined, cancelToken?: CancelToken): Promise<Project[]>;

  listHubs(includeLegacyHubs?: boolean | undefined, cancelToken?: CancelToken): Promise<Hub[]>;

  listImages(includeLegacyHubs?: boolean | undefined, cancelToken?: CancelToken): Promise<Project[]>;

  startProjectRvtVersionDetection(request?: StartProjectRevitVersionDetectionRequest | null | undefined, cancelToken?: CancelToken): Promise<ProjectRevitVersionDetectionResult>;

  createProjectWiseConfiguration(dto?: CreateProjectWiseConfigurationDto | null | undefined, cancelToken?: CancelToken): Promise<ProjectWiseConfiguration>;

  createProjectWiseCredential(dto?: CreateProjectWiseCredentialDto | null | undefined, cancelToken?: CancelToken): Promise<ProjectWiseCredential>;

  listProjectWiseCredentials(paginationToken?: string | null | undefined, limit?: number | undefined, cancelToken?: CancelToken): Promise<ProjectWiseCredentialPaginatedResult>;

  deleteProjectWiseConfiguration(id: string, cancelToken?: CancelToken): Promise<void>;

  getProjectWiseConfiguration(id: string, allowDeleted?: boolean | undefined, cancelToken?: CancelToken): Promise<ProjectWiseConfiguration>;

  updateProjectWiseConfiguration(id: string, dto?: UpdateProjectWiseConfigurationDto | null | undefined, cancelToken?: CancelToken): Promise<ProjectWiseConfiguration>;

  deleteProjectWiseCredential(id: string, cancelToken?: CancelToken): Promise<void>;

  getProjectWiseCredential(id: string, allowDeleted?: boolean | undefined, cancelToken?: CancelToken): Promise<ProjectWiseCredential>;

  updateProjectWiseCredential(id: string, dto?: UpdateProjectWiseCredentialDto | null | undefined, cancelToken?: CancelToken): Promise<ProjectWiseCredential>;

  getProjectWiseCredentialSecret(id: string, cancelToken?: CancelToken): Promise<ProjectWiseCredentialWithSecret>;

  listProjectWiseConfigurations(paginationToken?: string | null | undefined, limit?: number | undefined, cancelToken?: CancelToken): Promise<ProjectWiseConfigurationPaginatedResult>;

  listStorageTypes(cancelToken?: CancelToken): Promise<string[]>;

  getUserAccounts(cancelToken?: CancelToken): Promise<UserAccount[]>;

  getUserInfo(cancelToken?: CancelToken): Promise<UserBase>;

  /**
   * Performs a webhook callback.
   * @param webhookBody (optional) The webhook body.
   * @param signature (optional) The webhook signature.
   * @return A result indicating the status code.
   */
  userWebhookCallback(webhookBody?: GDPRWebhook | null | undefined, signature?: string | undefined, cancelToken?: CancelToken): Promise<GDPRWebhookResponse>;

  /**
   * Performs a webhook callback.
   * @param webhookBody (optional) The webhook body.
   * @param signature (optional) The webhook signature.
   * @param userId (optional) The user ID. Deprecated and will return 403 if present.
   * @return A result indicating the status code.
   */
  adskWebhookCallback(webhookBody?: WebhookBody | null | undefined, signature?: string | null | undefined, userId?: string | null | undefined, cancelToken?: CancelToken): Promise<void>;
}

export class Client implements IClient {
  protected instance: AxiosInstance;
  protected baseUrl: string;
  protected jsonParseReviver: ((key: string, value: any) => any) | undefined = undefined;

  constructor(baseUrl?: string, instance?: AxiosInstance) {

    this.instance = instance || axios.create();

    this.baseUrl = baseUrl ?? "";

  }

  /**
   * Callback for 3 legged authentication.
   * @param code (optional) The code returned by the authentication API.
   * @param callbackUrlOverride (optional) The callback URL, optionally.
   * @return An action result containing the authentication token.
   */
  callback(code?: string | undefined, callbackUrlOverride?: string | null | undefined, cancelToken?: CancelToken): Promise<DeckardAuthData> {
    let url_ = this.baseUrl + "/api/v1/auth/callback?";
    if (code === null)
      throw new globalThis.Error("The parameter 'code' cannot be null.");
    else if (code !== undefined)
      url_ += "code=" + encodeURIComponent("" + code) + "&";
    if (callbackUrlOverride !== undefined && callbackUrlOverride !== null)
      url_ += "callbackUrlOverride=" + encodeURIComponent("" + callbackUrlOverride) + "&";
    url_ = url_.replace(/[?&]$/, "");

    let options_: AxiosRequestConfig = {
      method: "POST",
      url: url_,
      headers: {
        "Accept": "application/json"
      },
      cancelToken
    };

    return this.instance.request(options_).catch((_error: any) => {
      if (isAxiosError(_error) && _error.response) {
        return _error.response;
      } else {
        throw _error;
      }
    }).then((_response: AxiosResponse) => {
      return this.processCallback(_response);
    });
  }

  protected processCallback(response: AxiosResponse): Promise<DeckardAuthData> {
    const status = response.status;
    let _headers: any = {};
    if (response.headers && typeof response.headers === "object") {
      for (const k in response.headers) {
        if (response.headers.hasOwnProperty(k)) {
          _headers[k] = response.headers[k];
        }
      }
    }
    if (status === 200) {
      const _responseText = response.data;
      let result200: any = null;
      let resultData200 = _responseText;
      result200 = DeckardAuthData.fromJS(resultData200);
      return Promise.resolve<DeckardAuthData>(result200);

    } else if (status === 401) {
      const _responseText = response.data;
      let result401: any = null;
      let resultData401 = _responseText;
      result401 = ProblemDetails.fromJS(resultData401);
      return throwException("A server side error occurred.", status, _responseText, _headers, result401);

    } else if (status !== 200 && status !== 204) {
      const _responseText = response.data;
      return throwException("An unexpected server error occurred.", status, _responseText, _headers);
    }
    return Promise.resolve<DeckardAuthData>(null as any);
  }

  /**
   * Endpoint to get a new access token using a refresh token.
   * @param userId The user id.
   * @param refreshToken The refresh token.
   * @return An action result containing the authentication token.
   */
  refreshToken(userId: string, refreshToken: string, cancelToken?: CancelToken): Promise<DeckardAuthData> {
    let url_ = this.baseUrl + "/api/v1/auth/refresh/{userId}/{refreshToken}";
    if (userId === undefined || userId === null)
      throw new globalThis.Error("The parameter 'userId' must be defined.");
    url_ = url_.replace("{userId}", encodeURIComponent("" + userId));
    if (refreshToken === undefined || refreshToken === null)
      throw new globalThis.Error("The parameter 'refreshToken' must be defined.");
    url_ = url_.replace("{refreshToken}", encodeURIComponent("" + refreshToken));
    url_ = url_.replace(/[?&]$/, "");

    let options_: AxiosRequestConfig = {
      method: "POST",
      url: url_,
      headers: {
        "Accept": "application/json"
      },
      cancelToken
    };

    return this.instance.request(options_).catch((_error: any) => {
      if (isAxiosError(_error) && _error.response) {
        return _error.response;
      } else {
        throw _error;
      }
    }).then((_response: AxiosResponse) => {
      return this.processRefreshToken(_response);
    });
  }

  protected processRefreshToken(response: AxiosResponse): Promise<DeckardAuthData> {
    const status = response.status;
    let _headers: any = {};
    if (response.headers && typeof response.headers === "object") {
      for (const k in response.headers) {
        if (response.headers.hasOwnProperty(k)) {
          _headers[k] = response.headers[k];
        }
      }
    }
    if (status === 200) {
      const _responseText = response.data;
      let result200: any = null;
      let resultData200 = _responseText;
      result200 = DeckardAuthData.fromJS(resultData200);
      return Promise.resolve<DeckardAuthData>(result200);

    } else if (status === 401) {
      const _responseText = response.data;
      let result401: any = null;
      let resultData401 = _responseText;
      result401 = ProblemDetails.fromJS(resultData401);
      return throwException("A server side error occurred.", status, _responseText, _headers, result401);

    } else if (status !== 200 && status !== 204) {
      const _responseText = response.data;
      return throwException("An unexpected server error occurred.", status, _responseText, _headers);
    }
    return Promise.resolve<DeckardAuthData>(null as any);
  }

  /**
   * Creates a job according to the information in the CreateJobCommand object.
   * @param createJobRequest (optional) The object defining the parameters of the job.
   * @return An object with information about the job.
   */
  createJob(createJobRequest?: CreateJobRequest | null | undefined, cancelToken?: CancelToken): Promise<Job> {
    let url_ = this.baseUrl + "/api/v1/job";
    url_ = url_.replace(/[?&]$/, "");

    const content_ = JSON.stringify(createJobRequest);

    let options_: AxiosRequestConfig = {
      data: content_,
      method: "POST",
      url: url_,
      headers: {
        "Content-Type": "application/json",
        "Accept": "application/json"
      },
      cancelToken
    };

    return this.instance.request(options_).catch((_error: any) => {
      if (isAxiosError(_error) && _error.response) {
        return _error.response;
      } else {
        throw _error;
      }
    }).then((_response: AxiosResponse) => {
      return this.processCreateJob(_response);
    });
  }

  protected processCreateJob(response: AxiosResponse): Promise<Job> {
    const status = response.status;
    let _headers: any = {};
    if (response.headers && typeof response.headers === "object") {
      for (const k in response.headers) {
        if (response.headers.hasOwnProperty(k)) {
          _headers[k] = response.headers[k];
        }
      }
    }
    if (status === 201) {
      const _responseText = response.data;
      let result201: any = null;
      let resultData201 = _responseText;
      result201 = Job.fromJS(resultData201);
      return Promise.resolve<Job>(result201);

    } else if (status === 400) {
      const _responseText = response.data;
      let result400: any = null;
      let resultData400 = _responseText;
      result400 = ProblemDetails.fromJS(resultData400);
      return throwException("A server side error occurred.", status, _responseText, _headers, result400);

    } else if (status === 422) {
      const _responseText = response.data;
      let result422: any = null;
      let resultData422 = _responseText;
      result422 = ProblemDetails.fromJS(resultData422);
      return throwException("A server side error occurred.", status, _responseText, _headers, result422);

    } else if (status !== 200 && status !== 204) {
      const _responseText = response.data;
      return throwException("An unexpected server error occurred.", status, _responseText, _headers);
    }
    return Promise.resolve<Job>(null as any);
  }

  /**
   * Gets all jobs for the current user.
   * @return An object with information about the job.
   */
  getJobs(cancelToken?: CancelToken): Promise<Job[]> {
    let url_ = this.baseUrl + "/api/v1/job";
    url_ = url_.replace(/[?&]$/, "");

    let options_: AxiosRequestConfig = {
      method: "GET",
      url: url_,
      headers: {
        "Accept": "application/json"
      },
      cancelToken
    };

    return this.instance.request(options_).catch((_error: any) => {
      if (isAxiosError(_error) && _error.response) {
        return _error.response;
      } else {
        throw _error;
      }
    }).then((_response: AxiosResponse) => {
      return this.processGetJobs(_response);
    });
  }

  protected processGetJobs(response: AxiosResponse): Promise<Job[]> {
    const status = response.status;
    let _headers: any = {};
    if (response.headers && typeof response.headers === "object") {
      for (const k in response.headers) {
        if (response.headers.hasOwnProperty(k)) {
          _headers[k] = response.headers[k];
        }
      }
    }
    if (status === 200) {
      const _responseText = response.data;
      let result200: any = null;
      let resultData200 = _responseText;
      if (Array.isArray(resultData200)) {
        result200 = [] as any;
        for (let item of resultData200)
          result200!.push(Job.fromJS(item));
      } else {
        result200 = null as any;
      }
      return Promise.resolve<Job[]>(result200);

    } else if (status !== 200 && status !== 204) {
      const _responseText = response.data;
      return throwException("An unexpected server error occurred.", status, _responseText, _headers);
    }
    return Promise.resolve<Job[]>(null as any);
  }

  /**
   * Gets the job with the given ID.
   * @param id The job ID.
   * @return An object with information about the job.
   */
  deleteJob(id: string, cancelToken?: CancelToken): Promise<void> {
    let url_ = this.baseUrl + "/api/v1/job/{id}";
    if (id === undefined || id === null)
      throw new globalThis.Error("The parameter 'id' must be defined.");
    url_ = url_.replace("{id}", encodeURIComponent("" + id));
    url_ = url_.replace(/[?&]$/, "");

    let options_: AxiosRequestConfig = {
      method: "DELETE",
      url: url_,
      headers: {},
      cancelToken
    };

    return this.instance.request(options_).catch((_error: any) => {
      if (isAxiosError(_error) && _error.response) {
        return _error.response;
      } else {
        throw _error;
      }
    }).then((_response: AxiosResponse) => {
      return this.processDeleteJob(_response);
    });
  }

  protected processDeleteJob(response: AxiosResponse): Promise<void> {
    const status = response.status;
    let _headers: any = {};
    if (response.headers && typeof response.headers === "object") {
      for (const k in response.headers) {
        if (response.headers.hasOwnProperty(k)) {
          _headers[k] = response.headers[k];
        }
      }
    }
    if (status === 204) {
      const _responseText = response.data;
      return Promise.resolve<void>(null as any);

    } else if (status === 400) {
      const _responseText = response.data;
      let result400: any = null;
      let resultData400 = _responseText;
      result400 = ProblemDetails.fromJS(resultData400);
      return throwException("A server side error occurred.", status, _responseText, _headers, result400);

    } else if (status === 404) {
      const _responseText = response.data;
      let result404: any = null;
      let resultData404 = _responseText;
      result404 = ProblemDetails.fromJS(resultData404);
      return throwException("A server side error occurred.", status, _responseText, _headers, result404);

    } else if (status !== 200 && status !== 204) {
      const _responseText = response.data;
      return throwException("An unexpected server error occurred.", status, _responseText, _headers);
    }
    return Promise.resolve<void>(null as any);
  }

  /**
   * Edits a job according to the information in the EditJobDto object.
   * @param id The job ID.
   * @param dto (optional) The DTO with the updated information.
   * @return An object with information about the job.
   */
  editJob(id: string, dto?: EditJobRequest | null | undefined, cancelToken?: CancelToken): Promise<Job> {
    let url_ = this.baseUrl + "/api/v1/job/{id}";
    if (id === undefined || id === null)
      throw new globalThis.Error("The parameter 'id' must be defined.");
    url_ = url_.replace("{id}", encodeURIComponent("" + id));
    url_ = url_.replace(/[?&]$/, "");

    const content_ = JSON.stringify(dto);

    let options_: AxiosRequestConfig = {
      data: content_,
      method: "PUT",
      url: url_,
      headers: {
        "Content-Type": "application/json",
        "Accept": "application/json"
      },
      cancelToken
    };

    return this.instance.request(options_).catch((_error: any) => {
      if (isAxiosError(_error) && _error.response) {
        return _error.response;
      } else {
        throw _error;
      }
    }).then((_response: AxiosResponse) => {
      return this.processEditJob(_response);
    });
  }

  protected processEditJob(response: AxiosResponse): Promise<Job> {
    const status = response.status;
    let _headers: any = {};
    if (response.headers && typeof response.headers === "object") {
      for (const k in response.headers) {
        if (response.headers.hasOwnProperty(k)) {
          _headers[k] = response.headers[k];
        }
      }
    }
    if (status === 200) {
      const _responseText = response.data;
      let result200: any = null;
      let resultData200 = _responseText;
      result200 = Job.fromJS(resultData200);
      return Promise.resolve<Job>(result200);

    } else if (status === 400) {
      const _responseText = response.data;
      let result400: any = null;
      let resultData400 = _responseText;
      result400 = ProblemDetails.fromJS(resultData400);
      return throwException("A server side error occurred.", status, _responseText, _headers, result400);

    } else if (status === 422) {
      const _responseText = response.data;
      let result422: any = null;
      let resultData422 = _responseText;
      result422 = ProblemDetails.fromJS(resultData422);
      return throwException("A server side error occurred.", status, _responseText, _headers, result422);

    } else if (status !== 200 && status !== 204) {
      const _responseText = response.data;
      return throwException("An unexpected server error occurred.", status, _responseText, _headers);
    }
    return Promise.resolve<Job>(null as any);
  }

  /**
   * Gets the job with the given ID.
   * @param id The job ID.
   * @param loadFilesAndFolders (optional) Whether to load the job files and folders. Defaults to true.
   * @param loadItems (optional) Whether to load the job items. Defaults to false.
   * @return An object with information about the job.
   */
  getJob(id: string, loadFilesAndFolders?: boolean | undefined, loadItems?: boolean | undefined, cancelToken?: CancelToken): Promise<Job> {
    let url_ = this.baseUrl + "/api/v1/job/{id}?";
    if (id === undefined || id === null)
      throw new globalThis.Error("The parameter 'id' must be defined.");
    url_ = url_.replace("{id}", encodeURIComponent("" + id));
    if (loadFilesAndFolders === null)
      throw new globalThis.Error("The parameter 'loadFilesAndFolders' cannot be null.");
    else if (loadFilesAndFolders !== undefined)
      url_ += "loadFilesAndFolders=" + encodeURIComponent("" + loadFilesAndFolders) + "&";
    if (loadItems === null)
      throw new globalThis.Error("The parameter 'loadItems' cannot be null.");
    else if (loadItems !== undefined)
      url_ += "loadItems=" + encodeURIComponent("" + loadItems) + "&";
    url_ = url_.replace(/[?&]$/, "");

    let options_: AxiosRequestConfig = {
      method: "GET",
      url: url_,
      headers: {
        "Accept": "application/json"
      },
      cancelToken
    };

    return this.instance.request(options_).catch((_error: any) => {
      if (isAxiosError(_error) && _error.response) {
        return _error.response;
      } else {
        throw _error;
      }
    }).then((_response: AxiosResponse) => {
      return this.processGetJob(_response);
    });
  }

  protected processGetJob(response: AxiosResponse): Promise<Job> {
    const status = response.status;
    let _headers: any = {};
    if (response.headers && typeof response.headers === "object") {
      for (const k in response.headers) {
        if (response.headers.hasOwnProperty(k)) {
          _headers[k] = response.headers[k];
        }
      }
    }
    if (status === 200) {
      const _responseText = response.data;
      let result200: any = null;
      let resultData200 = _responseText;
      result200 = Job.fromJS(resultData200);
      return Promise.resolve<Job>(result200);

    } else if (status === 404) {
      const _responseText = response.data;
      let result404: any = null;
      let resultData404 = _responseText;
      result404 = ProblemDetails.fromJS(resultData404);
      return throwException("A server side error occurred.", status, _responseText, _headers, result404);

    } else if (status !== 200 && status !== 204) {
      const _responseText = response.data;
      return throwException("An unexpected server error occurred.", status, _responseText, _headers);
    }
    return Promise.resolve<Job>(null as any);
  }

  /**
   * Gets a run for a job.
   * @param jobRunId The job run id.
   * @return An object with information about the job.
   */
  getJobRun(jobRunId: string, cancelToken?: CancelToken): Promise<JobRun> {
    let url_ = this.baseUrl + "/api/v1/job/status/{jobRunId}";
    if (jobRunId === undefined || jobRunId === null)
      throw new globalThis.Error("The parameter 'jobRunId' must be defined.");
    url_ = url_.replace("{jobRunId}", encodeURIComponent("" + jobRunId));
    url_ = url_.replace(/[?&]$/, "");

    let options_: AxiosRequestConfig = {
      method: "GET",
      url: url_,
      headers: {
        "Accept": "application/json"
      },
      cancelToken
    };

    return this.instance.request(options_).catch((_error: any) => {
      if (isAxiosError(_error) && _error.response) {
        return _error.response;
      } else {
        throw _error;
      }
    }).then((_response: AxiosResponse) => {
      return this.processGetJobRun(_response);
    });
  }

  protected processGetJobRun(response: AxiosResponse): Promise<JobRun> {
    const status = response.status;
    let _headers: any = {};
    if (response.headers && typeof response.headers === "object") {
      for (const k in response.headers) {
        if (response.headers.hasOwnProperty(k)) {
          _headers[k] = response.headers[k];
        }
      }
    }
    if (status === 200) {
      const _responseText = response.data;
      let result200: any = null;
      let resultData200 = _responseText;
      result200 = JobRun.fromJS(resultData200);
      return Promise.resolve<JobRun>(result200);

    } else if (status === 400) {
      const _responseText = response.data;
      let result400: any = null;
      let resultData400 = _responseText;
      result400 = ProblemDetails.fromJS(resultData400);
      return throwException("A server side error occurred.", status, _responseText, _headers, result400);

    } else if (status === 404) {
      const _responseText = response.data;
      let result404: any = null;
      let resultData404 = _responseText;
      result404 = ProblemDetails.fromJS(resultData404);
      return throwException("A server side error occurred.", status, _responseText, _headers, result404);

    } else if (status !== 200 && status !== 204) {
      const _responseText = response.data;
      return throwException("An unexpected server error occurred.", status, _responseText, _headers);
    }
    return Promise.resolve<JobRun>(null as any);
  }

  /**
   * Performs a job callback.
   * @param accessKey (optional) The access key to ensure that this request is performed by the callback.
   * @param id (optional) The job id.
   * @param result (optional) The job result.
   * @return A result indicating the status code.
   */
  jobCallback(accessKey?: string | undefined, id?: string | null | undefined, result?: any | null | undefined, cancelToken?: CancelToken): Promise<void> {
    let url_ = this.baseUrl + "/api/v1/job/jobcallback?";
    if (accessKey === null)
      throw new globalThis.Error("The parameter 'accessKey' cannot be null.");
    else if (accessKey !== undefined)
      url_ += "accessKey=" + encodeURIComponent("" + accessKey) + "&";
    if (id !== undefined && id !== null)
      url_ += "id=" + encodeURIComponent("" + id) + "&";
    url_ = url_.replace(/[?&]$/, "");

    const content_ = JSON.stringify(result);

    let options_: AxiosRequestConfig = {
      data: content_,
      method: "POST",
      url: url_,
      headers: {
        "Content-Type": "application/json",
      },
      cancelToken
    };

    return this.instance.request(options_).catch((_error: any) => {
      if (isAxiosError(_error) && _error.response) {
        return _error.response;
      } else {
        throw _error;
      }
    }).then((_response: AxiosResponse) => {
      return this.processJobCallback(_response);
    });
  }

  protected processJobCallback(response: AxiosResponse): Promise<void> {
    const status = response.status;
    let _headers: any = {};
    if (response.headers && typeof response.headers === "object") {
      for (const k in response.headers) {
        if (response.headers.hasOwnProperty(k)) {
          _headers[k] = response.headers[k];
        }
      }
    }
    if (status === 200) {
      const _responseText = response.data;
      return Promise.resolve<void>(null as any);

    } else if (status !== 200 && status !== 204) {
      const _responseText = response.data;
      return throwException("An unexpected server error occurred.", status, _responseText, _headers);
    }
    return Promise.resolve<void>(null as any);
  }

  /**
   * Starts a job according to the information in the StartJobCommand object.
   * @param jobId The job id.
   * @return An object with information about the job.
   */
  startJob(jobId: string, cancelToken?: CancelToken): Promise<JobRun> {
    let url_ = this.baseUrl + "/api/v1/job/start/{jobId}";
    if (jobId === undefined || jobId === null)
      throw new globalThis.Error("The parameter 'jobId' must be defined.");
    url_ = url_.replace("{jobId}", encodeURIComponent("" + jobId));
    url_ = url_.replace(/[?&]$/, "");

    let options_: AxiosRequestConfig = {
      method: "POST",
      url: url_,
      headers: {
        "Accept": "application/json"
      },
      cancelToken
    };

    return this.instance.request(options_).catch((_error: any) => {
      if (isAxiosError(_error) && _error.response) {
        return _error.response;
      } else {
        throw _error;
      }
    }).then((_response: AxiosResponse) => {
      return this.processStartJob(_response);
    });
  }

  protected processStartJob(response: AxiosResponse): Promise<JobRun> {
    const status = response.status;
    let _headers: any = {};
    if (response.headers && typeof response.headers === "object") {
      for (const k in response.headers) {
        if (response.headers.hasOwnProperty(k)) {
          _headers[k] = response.headers[k];
        }
      }
    }
    if (status === 200) {
      const _responseText = response.data;
      let result200: any = null;
      let resultData200 = _responseText;
      result200 = JobRun.fromJS(resultData200);
      return Promise.resolve<JobRun>(result200);

    } else if (status === 400) {
      const _responseText = response.data;
      let result400: any = null;
      let resultData400 = _responseText;
      result400 = ProblemDetails.fromJS(resultData400);
      return throwException("A server side error occurred.", status, _responseText, _headers, result400);

    } else if (status === 404) {
      const _responseText = response.data;
      let result404: any = null;
      let resultData404 = _responseText;
      result404 = ProblemDetails.fromJS(resultData404);
      return throwException("A server side error occurred.", status, _responseText, _headers, result404);

    } else if (status !== 200 && status !== 204) {
      const _responseText = response.data;
      return throwException("An unexpected server error occurred.", status, _responseText, _headers);
    }
    return Promise.resolve<JobRun>(null as any);
  }

  getCsvDownload(id?: string | undefined, cancelToken?: CancelToken): Promise<DownloadDto> {
    let url_ = this.baseUrl + "/api/v1/usage/csv/status?";
    if (id === null)
      throw new globalThis.Error("The parameter 'id' cannot be null.");
    else if (id !== undefined)
      url_ += "id=" + encodeURIComponent("" + id) + "&";
    url_ = url_.replace(/[?&]$/, "");

    let options_: AxiosRequestConfig = {
      method: "GET",
      url: url_,
      headers: {
        "Accept": "application/json"
      },
      cancelToken
    };

    return this.instance.request(options_).catch((_error: any) => {
      if (isAxiosError(_error) && _error.response) {
        return _error.response;
      } else {
        throw _error;
      }
    }).then((_response: AxiosResponse) => {
      return this.processGetCsvDownload(_response);
    });
  }

  protected processGetCsvDownload(response: AxiosResponse): Promise<DownloadDto> {
    const status = response.status;
    let _headers: any = {};
    if (response.headers && typeof response.headers === "object") {
      for (const k in response.headers) {
        if (response.headers.hasOwnProperty(k)) {
          _headers[k] = response.headers[k];
        }
      }
    }
    if (status === 200) {
      const _responseText = response.data;
      let result200: any = null;
      let resultData200 = _responseText;
      result200 = DownloadDto.fromJS(resultData200);
      return Promise.resolve<DownloadDto>(result200);

    } else if (status !== 200 && status !== 204) {
      const _responseText = response.data;
      return throwException("An unexpected server error occurred.", status, _responseText, _headers);
    }
    return Promise.resolve<DownloadDto>(null as any);
  }

  getDetailedUsage(statuses?: JobRunItemStatusType[] | undefined, limit?: number | null | undefined, paginationToken?: string | null | undefined, filter_CustomerIds?: string[] | undefined, filter_UserIds?: string[] | undefined, filter_JobIds?: string[] | undefined, filter_JobRunIds?: string[] | undefined, filter_StartTime?: Date | null | undefined, filter_EndTime?: Date | null | undefined, sort_SortType?: UsageSortType | undefined, sort_Ascending?: boolean | undefined, options_IncludeFullErrorInfo?: boolean | undefined, options_FileEntryLimit?: number | undefined, exportType?: UsageExportType | undefined, exportFormat?: UsageExportFormat | undefined, writeIfEmpty?: boolean | undefined, cancelToken?: CancelToken): Promise<DetailedUsage> {
    let url_ = this.baseUrl + "/api/v1/usage/detail?";
    if (statuses === null)
      throw new globalThis.Error("The parameter 'statuses' cannot be null.");
    else if (statuses !== undefined)
      statuses && statuses.forEach(item => {
        url_ += "Statuses=" + encodeURIComponent("" + item) + "&";
      });
    if (limit !== undefined && limit !== null)
      url_ += "Limit=" + encodeURIComponent("" + limit) + "&";
    if (paginationToken !== undefined && paginationToken !== null)
      url_ += "PaginationToken=" + encodeURIComponent("" + paginationToken) + "&";
    if (filter_CustomerIds === null)
      throw new globalThis.Error("The parameter 'filter_CustomerIds' cannot be null.");
    else if (filter_CustomerIds !== undefined)
      filter_CustomerIds && filter_CustomerIds.forEach(item => {
        url_ += "Filter.CustomerIds=" + encodeURIComponent("" + item) + "&";
      });
    if (filter_UserIds === null)
      throw new globalThis.Error("The parameter 'filter_UserIds' cannot be null.");
    else if (filter_UserIds !== undefined)
      filter_UserIds && filter_UserIds.forEach(item => {
        url_ += "Filter.UserIds=" + encodeURIComponent("" + item) + "&";
      });
    if (filter_JobIds === null)
      throw new globalThis.Error("The parameter 'filter_JobIds' cannot be null.");
    else if (filter_JobIds !== undefined)
      filter_JobIds && filter_JobIds.forEach(item => {
        url_ += "Filter.JobIds=" + encodeURIComponent("" + item) + "&";
      });
    if (filter_JobRunIds === null)
      throw new globalThis.Error("The parameter 'filter_JobRunIds' cannot be null.");
    else if (filter_JobRunIds !== undefined)
      filter_JobRunIds && filter_JobRunIds.forEach(item => {
        url_ += "Filter.JobRunIds=" + encodeURIComponent("" + item) + "&";
      });
    if (filter_StartTime !== undefined && filter_StartTime !== null)
      url_ += "Filter.StartTime=" + encodeURIComponent(filter_StartTime ? "" + filter_StartTime.toISOString() : "") + "&";
    if (filter_EndTime !== undefined && filter_EndTime !== null)
      url_ += "Filter.EndTime=" + encodeURIComponent(filter_EndTime ? "" + filter_EndTime.toISOString() : "") + "&";
    if (sort_SortType === null)
      throw new globalThis.Error("The parameter 'sort_SortType' cannot be null.");
    else if (sort_SortType !== undefined)
      url_ += "Sort.SortType=" + encodeURIComponent("" + sort_SortType) + "&";
    if (sort_Ascending === null)
      throw new globalThis.Error("The parameter 'sort_Ascending' cannot be null.");
    else if (sort_Ascending !== undefined)
      url_ += "Sort.Ascending=" + encodeURIComponent("" + sort_Ascending) + "&";
    if (options_IncludeFullErrorInfo === null)
      throw new globalThis.Error("The parameter 'options_IncludeFullErrorInfo' cannot be null.");
    else if (options_IncludeFullErrorInfo !== undefined)
      url_ += "Options.IncludeFullErrorInfo=" + encodeURIComponent("" + options_IncludeFullErrorInfo) + "&";
    if (options_FileEntryLimit === null)
      throw new globalThis.Error("The parameter 'options_FileEntryLimit' cannot be null.");
    else if (options_FileEntryLimit !== undefined)
      url_ += "Options.FileEntryLimit=" + encodeURIComponent("" + options_FileEntryLimit) + "&";
    if (exportType === null)
      throw new globalThis.Error("The parameter 'exportType' cannot be null.");
    else if (exportType !== undefined)
      url_ += "ExportType=" + encodeURIComponent("" + exportType) + "&";
    if (exportFormat === null)
      throw new globalThis.Error("The parameter 'exportFormat' cannot be null.");
    else if (exportFormat !== undefined)
      url_ += "ExportFormat=" + encodeURIComponent("" + exportFormat) + "&";
    if (writeIfEmpty === null)
      throw new globalThis.Error("The parameter 'writeIfEmpty' cannot be null.");
    else if (writeIfEmpty !== undefined)
      url_ += "WriteIfEmpty=" + encodeURIComponent("" + writeIfEmpty) + "&";
    url_ = url_.replace(/[?&]$/, "");

    let options_: AxiosRequestConfig = {
      method: "GET",
      url: url_,
      headers: {
        "Accept": "application/json"
      },
      cancelToken
    };

    return this.instance.request(options_).catch((_error: any) => {
      if (isAxiosError(_error) && _error.response) {
        return _error.response;
      } else {
        throw _error;
      }
    }).then((_response: AxiosResponse) => {
      return this.processGetDetailedUsage(_response);
    });
  }

  protected processGetDetailedUsage(response: AxiosResponse): Promise<DetailedUsage> {
    const status = response.status;
    let _headers: any = {};
    if (response.headers && typeof response.headers === "object") {
      for (const k in response.headers) {
        if (response.headers.hasOwnProperty(k)) {
          _headers[k] = response.headers[k];
        }
      }
    }
    if (status === 200) {
      const _responseText = response.data;
      let result200: any = null;
      let resultData200 = _responseText;
      result200 = DetailedUsage.fromJS(resultData200);
      return Promise.resolve<DetailedUsage>(result200);

    } else if (status !== 200 && status !== 204) {
      const _responseText = response.data;
      return throwException("An unexpected server error occurred.", status, _responseText, _headers);
    }
    return Promise.resolve<DetailedUsage>(null as any);
  }

  getUsageDownload(id?: string | undefined, cancelToken?: CancelToken): Promise<DownloadDto> {
    let url_ = this.baseUrl + "/api/v1/usage/download/status?";
    if (id === null)
      throw new globalThis.Error("The parameter 'id' cannot be null.");
    else if (id !== undefined)
      url_ += "id=" + encodeURIComponent("" + id) + "&";
    url_ = url_.replace(/[?&]$/, "");

    let options_: AxiosRequestConfig = {
      method: "GET",
      url: url_,
      headers: {
        "Accept": "application/json"
      },
      cancelToken
    };

    return this.instance.request(options_).catch((_error: any) => {
      if (isAxiosError(_error) && _error.response) {
        return _error.response;
      } else {
        throw _error;
      }
    }).then((_response: AxiosResponse) => {
      return this.processGetUsageDownload(_response);
    });
  }

  protected processGetUsageDownload(response: AxiosResponse): Promise<DownloadDto> {
    const status = response.status;
    let _headers: any = {};
    if (response.headers && typeof response.headers === "object") {
      for (const k in response.headers) {
        if (response.headers.hasOwnProperty(k)) {
          _headers[k] = response.headers[k];
        }
      }
    }
    if (status === 200) {
      const _responseText = response.data;
      let result200: any = null;
      let resultData200 = _responseText;
      result200 = DownloadDto.fromJS(resultData200);
      return Promise.resolve<DownloadDto>(result200);

    } else if (status !== 200 && status !== 204) {
      const _responseText = response.data;
      return throwException("An unexpected server error occurred.", status, _responseText, _headers);
    }
    return Promise.resolve<DownloadDto>(null as any);
  }

  getUsageSummary(statuses?: JobRunStatusType[] | undefined, includeRuns?: boolean | undefined, limit?: number | null | undefined, paginationToken?: string | null | undefined, filter_CustomerIds?: string[] | undefined, filter_UserIds?: string[] | undefined, filter_JobIds?: string[] | undefined, filter_JobRunIds?: string[] | undefined, filter_StartTime?: Date | null | undefined, filter_EndTime?: Date | null | undefined, sort_SortType?: UsageSortType | undefined, sort_Ascending?: boolean | undefined, options_IncludeFullErrorInfo?: boolean | undefined, options_FileEntryLimit?: number | undefined, exportType?: UsageExportType | undefined, exportFormat?: UsageExportFormat | undefined, writeIfEmpty?: boolean | undefined, cancelToken?: CancelToken): Promise<UsageSummary> {
    let url_ = this.baseUrl + "/api/v1/usage/summary?";
    if (statuses === null)
      throw new globalThis.Error("The parameter 'statuses' cannot be null.");
    else if (statuses !== undefined)
      statuses && statuses.forEach(item => {
        url_ += "Statuses=" + encodeURIComponent("" + item) + "&";
      });
    if (includeRuns === null)
      throw new globalThis.Error("The parameter 'includeRuns' cannot be null.");
    else if (includeRuns !== undefined)
      url_ += "IncludeRuns=" + encodeURIComponent("" + includeRuns) + "&";
    if (limit !== undefined && limit !== null)
      url_ += "Limit=" + encodeURIComponent("" + limit) + "&";
    if (paginationToken !== undefined && paginationToken !== null)
      url_ += "PaginationToken=" + encodeURIComponent("" + paginationToken) + "&";
    if (filter_CustomerIds === null)
      throw new globalThis.Error("The parameter 'filter_CustomerIds' cannot be null.");
    else if (filter_CustomerIds !== undefined)
      filter_CustomerIds && filter_CustomerIds.forEach(item => {
        url_ += "Filter.CustomerIds=" + encodeURIComponent("" + item) + "&";
      });
    if (filter_UserIds === null)
      throw new globalThis.Error("The parameter 'filter_UserIds' cannot be null.");
    else if (filter_UserIds !== undefined)
      filter_UserIds && filter_UserIds.forEach(item => {
        url_ += "Filter.UserIds=" + encodeURIComponent("" + item) + "&";
      });
    if (filter_JobIds === null)
      throw new globalThis.Error("The parameter 'filter_JobIds' cannot be null.");
    else if (filter_JobIds !== undefined)
      filter_JobIds && filter_JobIds.forEach(item => {
        url_ += "Filter.JobIds=" + encodeURIComponent("" + item) + "&";
      });
    if (filter_JobRunIds === null)
      throw new globalThis.Error("The parameter 'filter_JobRunIds' cannot be null.");
    else if (filter_JobRunIds !== undefined)
      filter_JobRunIds && filter_JobRunIds.forEach(item => {
        url_ += "Filter.JobRunIds=" + encodeURIComponent("" + item) + "&";
      });
    if (filter_StartTime !== undefined && filter_StartTime !== null)
      url_ += "Filter.StartTime=" + encodeURIComponent(filter_StartTime ? "" + filter_StartTime.toISOString() : "") + "&";
    if (filter_EndTime !== undefined && filter_EndTime !== null)
      url_ += "Filter.EndTime=" + encodeURIComponent(filter_EndTime ? "" + filter_EndTime.toISOString() : "") + "&";
    if (sort_SortType === null)
      throw new globalThis.Error("The parameter 'sort_SortType' cannot be null.");
    else if (sort_SortType !== undefined)
      url_ += "Sort.SortType=" + encodeURIComponent("" + sort_SortType) + "&";
    if (sort_Ascending === null)
      throw new globalThis.Error("The parameter 'sort_Ascending' cannot be null.");
    else if (sort_Ascending !== undefined)
      url_ += "Sort.Ascending=" + encodeURIComponent("" + sort_Ascending) + "&";
    if (options_IncludeFullErrorInfo === null)
      throw new globalThis.Error("The parameter 'options_IncludeFullErrorInfo' cannot be null.");
    else if (options_IncludeFullErrorInfo !== undefined)
      url_ += "Options.IncludeFullErrorInfo=" + encodeURIComponent("" + options_IncludeFullErrorInfo) + "&";
    if (options_FileEntryLimit === null)
      throw new globalThis.Error("The parameter 'options_FileEntryLimit' cannot be null.");
    else if (options_FileEntryLimit !== undefined)
      url_ += "Options.FileEntryLimit=" + encodeURIComponent("" + options_FileEntryLimit) + "&";
    if (exportType === null)
      throw new globalThis.Error("The parameter 'exportType' cannot be null.");
    else if (exportType !== undefined)
      url_ += "ExportType=" + encodeURIComponent("" + exportType) + "&";
    if (exportFormat === null)
      throw new globalThis.Error("The parameter 'exportFormat' cannot be null.");
    else if (exportFormat !== undefined)
      url_ += "ExportFormat=" + encodeURIComponent("" + exportFormat) + "&";
    if (writeIfEmpty === null)
      throw new globalThis.Error("The parameter 'writeIfEmpty' cannot be null.");
    else if (writeIfEmpty !== undefined)
      url_ += "WriteIfEmpty=" + encodeURIComponent("" + writeIfEmpty) + "&";
    url_ = url_.replace(/[?&]$/, "");

    let options_: AxiosRequestConfig = {
      method: "GET",
      url: url_,
      headers: {
        "Accept": "application/json"
      },
      cancelToken
    };

    return this.instance.request(options_).catch((_error: any) => {
      if (isAxiosError(_error) && _error.response) {
        return _error.response;
      } else {
        throw _error;
      }
    }).then((_response: AxiosResponse) => {
      return this.processGetUsageSummary(_response);
    });
  }

  protected processGetUsageSummary(response: AxiosResponse): Promise<UsageSummary> {
    const status = response.status;
    let _headers: any = {};
    if (response.headers && typeof response.headers === "object") {
      for (const k in response.headers) {
        if (response.headers.hasOwnProperty(k)) {
          _headers[k] = response.headers[k];
        }
      }
    }
    if (status === 200) {
      const _responseText = response.data;
      let result200: any = null;
      let resultData200 = _responseText;
      result200 = UsageSummary.fromJS(resultData200);
      return Promise.resolve<UsageSummary>(result200);

    } else if (status !== 200 && status !== 204) {
      const _responseText = response.data;
      return throwException("An unexpected server error occurred.", status, _responseText, _headers);
    }
    return Promise.resolve<UsageSummary>(null as any);
  }

  startCsvDownload(filter_CustomerIds?: string[] | undefined, filter_UserIds?: string[] | undefined, filter_JobIds?: string[] | undefined, filter_JobRunIds?: string[] | undefined, filter_StartTime?: Date | null | undefined, filter_EndTime?: Date | null | undefined, sort_SortType?: UsageSortType | undefined, sort_Ascending?: boolean | undefined, options_IncludeFullErrorInfo?: boolean | undefined, options_FileEntryLimit?: number | undefined, exportType?: UsageExportType | undefined, exportFormat?: UsageExportFormat | undefined, writeIfEmpty?: boolean | undefined, cancelToken?: CancelToken): Promise<StartUsageDownloadResult> {
    let url_ = this.baseUrl + "/api/v1/usage/csv/start?";
    if (filter_CustomerIds === null)
      throw new globalThis.Error("The parameter 'filter_CustomerIds' cannot be null.");
    else if (filter_CustomerIds !== undefined)
      filter_CustomerIds && filter_CustomerIds.forEach(item => {
        url_ += "Filter.CustomerIds=" + encodeURIComponent("" + item) + "&";
      });
    if (filter_UserIds === null)
      throw new globalThis.Error("The parameter 'filter_UserIds' cannot be null.");
    else if (filter_UserIds !== undefined)
      filter_UserIds && filter_UserIds.forEach(item => {
        url_ += "Filter.UserIds=" + encodeURIComponent("" + item) + "&";
      });
    if (filter_JobIds === null)
      throw new globalThis.Error("The parameter 'filter_JobIds' cannot be null.");
    else if (filter_JobIds !== undefined)
      filter_JobIds && filter_JobIds.forEach(item => {
        url_ += "Filter.JobIds=" + encodeURIComponent("" + item) + "&";
      });
    if (filter_JobRunIds === null)
      throw new globalThis.Error("The parameter 'filter_JobRunIds' cannot be null.");
    else if (filter_JobRunIds !== undefined)
      filter_JobRunIds && filter_JobRunIds.forEach(item => {
        url_ += "Filter.JobRunIds=" + encodeURIComponent("" + item) + "&";
      });
    if (filter_StartTime !== undefined && filter_StartTime !== null)
      url_ += "Filter.StartTime=" + encodeURIComponent(filter_StartTime ? "" + filter_StartTime.toISOString() : "") + "&";
    if (filter_EndTime !== undefined && filter_EndTime !== null)
      url_ += "Filter.EndTime=" + encodeURIComponent(filter_EndTime ? "" + filter_EndTime.toISOString() : "") + "&";
    if (sort_SortType === null)
      throw new globalThis.Error("The parameter 'sort_SortType' cannot be null.");
    else if (sort_SortType !== undefined)
      url_ += "Sort.SortType=" + encodeURIComponent("" + sort_SortType) + "&";
    if (sort_Ascending === null)
      throw new globalThis.Error("The parameter 'sort_Ascending' cannot be null.");
    else if (sort_Ascending !== undefined)
      url_ += "Sort.Ascending=" + encodeURIComponent("" + sort_Ascending) + "&";
    if (options_IncludeFullErrorInfo === null)
      throw new globalThis.Error("The parameter 'options_IncludeFullErrorInfo' cannot be null.");
    else if (options_IncludeFullErrorInfo !== undefined)
      url_ += "Options.IncludeFullErrorInfo=" + encodeURIComponent("" + options_IncludeFullErrorInfo) + "&";
    if (options_FileEntryLimit === null)
      throw new globalThis.Error("The parameter 'options_FileEntryLimit' cannot be null.");
    else if (options_FileEntryLimit !== undefined)
      url_ += "Options.FileEntryLimit=" + encodeURIComponent("" + options_FileEntryLimit) + "&";
    if (exportType === null)
      throw new globalThis.Error("The parameter 'exportType' cannot be null.");
    else if (exportType !== undefined)
      url_ += "ExportType=" + encodeURIComponent("" + exportType) + "&";
    if (exportFormat === null)
      throw new globalThis.Error("The parameter 'exportFormat' cannot be null.");
    else if (exportFormat !== undefined)
      url_ += "ExportFormat=" + encodeURIComponent("" + exportFormat) + "&";
    if (writeIfEmpty === null)
      throw new globalThis.Error("The parameter 'writeIfEmpty' cannot be null.");
    else if (writeIfEmpty !== undefined)
      url_ += "WriteIfEmpty=" + encodeURIComponent("" + writeIfEmpty) + "&";
    url_ = url_.replace(/[?&]$/, "");

    let options_: AxiosRequestConfig = {
      method: "POST",
      url: url_,
      headers: {
        "Accept": "application/json"
      },
      cancelToken
    };

    return this.instance.request(options_).catch((_error: any) => {
      if (isAxiosError(_error) && _error.response) {
        return _error.response;
      } else {
        throw _error;
      }
    }).then((_response: AxiosResponse) => {
      return this.processStartCsvDownload(_response);
    });
  }

  protected processStartCsvDownload(response: AxiosResponse): Promise<StartUsageDownloadResult> {
    const status = response.status;
    let _headers: any = {};
    if (response.headers && typeof response.headers === "object") {
      for (const k in response.headers) {
        if (response.headers.hasOwnProperty(k)) {
          _headers[k] = response.headers[k];
        }
      }
    }
    if (status === 200) {
      const _responseText = response.data;
      let result200: any = null;
      let resultData200 = _responseText;
      result200 = StartUsageDownloadResult.fromJS(resultData200);
      return Promise.resolve<StartUsageDownloadResult>(result200);

    } else if (status !== 200 && status !== 204) {
      const _responseText = response.data;
      return throwException("An unexpected server error occurred.", status, _responseText, _headers);
    }
    return Promise.resolve<StartUsageDownloadResult>(null as any);
  }

  startUsageDownload(filter_CustomerIds?: string[] | undefined, filter_UserIds?: string[] | undefined, filter_JobIds?: string[] | undefined, filter_JobRunIds?: string[] | undefined, filter_StartTime?: Date | null | undefined, filter_EndTime?: Date | null | undefined, sort_SortType?: UsageSortType | undefined, sort_Ascending?: boolean | undefined, options_IncludeFullErrorInfo?: boolean | undefined, options_FileEntryLimit?: number | undefined, exportType?: UsageExportType | undefined, exportFormat?: UsageExportFormat | undefined, writeIfEmpty?: boolean | undefined, cancelToken?: CancelToken): Promise<StartUsageDownloadResult> {
    let url_ = this.baseUrl + "/api/v1/usage/download/start?";
    if (filter_CustomerIds === null)
      throw new globalThis.Error("The parameter 'filter_CustomerIds' cannot be null.");
    else if (filter_CustomerIds !== undefined)
      filter_CustomerIds && filter_CustomerIds.forEach(item => {
        url_ += "Filter.CustomerIds=" + encodeURIComponent("" + item) + "&";
      });
    if (filter_UserIds === null)
      throw new globalThis.Error("The parameter 'filter_UserIds' cannot be null.");
    else if (filter_UserIds !== undefined)
      filter_UserIds && filter_UserIds.forEach(item => {
        url_ += "Filter.UserIds=" + encodeURIComponent("" + item) + "&";
      });
    if (filter_JobIds === null)
      throw new globalThis.Error("The parameter 'filter_JobIds' cannot be null.");
    else if (filter_JobIds !== undefined)
      filter_JobIds && filter_JobIds.forEach(item => {
        url_ += "Filter.JobIds=" + encodeURIComponent("" + item) + "&";
      });
    if (filter_JobRunIds === null)
      throw new globalThis.Error("The parameter 'filter_JobRunIds' cannot be null.");
    else if (filter_JobRunIds !== undefined)
      filter_JobRunIds && filter_JobRunIds.forEach(item => {
        url_ += "Filter.JobRunIds=" + encodeURIComponent("" + item) + "&";
      });
    if (filter_StartTime !== undefined && filter_StartTime !== null)
      url_ += "Filter.StartTime=" + encodeURIComponent(filter_StartTime ? "" + filter_StartTime.toISOString() : "") + "&";
    if (filter_EndTime !== undefined && filter_EndTime !== null)
      url_ += "Filter.EndTime=" + encodeURIComponent(filter_EndTime ? "" + filter_EndTime.toISOString() : "") + "&";
    if (sort_SortType === null)
      throw new globalThis.Error("The parameter 'sort_SortType' cannot be null.");
    else if (sort_SortType !== undefined)
      url_ += "Sort.SortType=" + encodeURIComponent("" + sort_SortType) + "&";
    if (sort_Ascending === null)
      throw new globalThis.Error("The parameter 'sort_Ascending' cannot be null.");
    else if (sort_Ascending !== undefined)
      url_ += "Sort.Ascending=" + encodeURIComponent("" + sort_Ascending) + "&";
    if (options_IncludeFullErrorInfo === null)
      throw new globalThis.Error("The parameter 'options_IncludeFullErrorInfo' cannot be null.");
    else if (options_IncludeFullErrorInfo !== undefined)
      url_ += "Options.IncludeFullErrorInfo=" + encodeURIComponent("" + options_IncludeFullErrorInfo) + "&";
    if (options_FileEntryLimit === null)
      throw new globalThis.Error("The parameter 'options_FileEntryLimit' cannot be null.");
    else if (options_FileEntryLimit !== undefined)
      url_ += "Options.FileEntryLimit=" + encodeURIComponent("" + options_FileEntryLimit) + "&";
    if (exportType === null)
      throw new globalThis.Error("The parameter 'exportType' cannot be null.");
    else if (exportType !== undefined)
      url_ += "ExportType=" + encodeURIComponent("" + exportType) + "&";
    if (exportFormat === null)
      throw new globalThis.Error("The parameter 'exportFormat' cannot be null.");
    else if (exportFormat !== undefined)
      url_ += "ExportFormat=" + encodeURIComponent("" + exportFormat) + "&";
    if (writeIfEmpty === null)
      throw new globalThis.Error("The parameter 'writeIfEmpty' cannot be null.");
    else if (writeIfEmpty !== undefined)
      url_ += "WriteIfEmpty=" + encodeURIComponent("" + writeIfEmpty) + "&";
    url_ = url_.replace(/[?&]$/, "");

    let options_: AxiosRequestConfig = {
      method: "POST",
      url: url_,
      headers: {
        "Accept": "application/json"
      },
      cancelToken
    };

    return this.instance.request(options_).catch((_error: any) => {
      if (isAxiosError(_error) && _error.response) {
        return _error.response;
      } else {
        throw _error;
      }
    }).then((_response: AxiosResponse) => {
      return this.processStartUsageDownload(_response);
    });
  }

  protected processStartUsageDownload(response: AxiosResponse): Promise<StartUsageDownloadResult> {
    const status = response.status;
    let _headers: any = {};
    if (response.headers && typeof response.headers === "object") {
      for (const k in response.headers) {
        if (response.headers.hasOwnProperty(k)) {
          _headers[k] = response.headers[k];
        }
      }
    }
    if (status === 200) {
      const _responseText = response.data;
      let result200: any = null;
      let resultData200 = _responseText;
      result200 = StartUsageDownloadResult.fromJS(resultData200);
      return Promise.resolve<StartUsageDownloadResult>(result200);

    } else if (status !== 200 && status !== 204) {
      const _responseText = response.data;
      return throwException("An unexpected server error occurred.", status, _responseText, _headers);
    }
    return Promise.resolve<StartUsageDownloadResult>(null as any);
  }

  /**
   * Creates a webhook according to the information in the DTO object.
   * @param createWebhookDto (optional) The object defining the parameters of the webhook.
   * @return An object with information about the webhook.
   */
  createWebhook(createWebhookDto?: CreateOutgoingWebhookRequest | null | undefined, cancelToken?: CancelToken): Promise<OutgoingWebhook> {
    let url_ = this.baseUrl + "/api/v1/webhook";
    url_ = url_.replace(/[?&]$/, "");

    const content_ = JSON.stringify(createWebhookDto);

    let options_: AxiosRequestConfig = {
      data: content_,
      method: "POST",
      url: url_,
      headers: {
        "Content-Type": "application/json",
        "Accept": "application/json"
      },
      cancelToken
    };

    return this.instance.request(options_).catch((_error: any) => {
      if (isAxiosError(_error) && _error.response) {
        return _error.response;
      } else {
        throw _error;
      }
    }).then((_response: AxiosResponse) => {
      return this.processCreateWebhook(_response);
    });
  }

  protected processCreateWebhook(response: AxiosResponse): Promise<OutgoingWebhook> {
    const status = response.status;
    let _headers: any = {};
    if (response.headers && typeof response.headers === "object") {
      for (const k in response.headers) {
        if (response.headers.hasOwnProperty(k)) {
          _headers[k] = response.headers[k];
        }
      }
    }
    if (status === 201) {
      const _responseText = response.data;
      let result201: any = null;
      let resultData201 = _responseText;
      result201 = OutgoingWebhook.fromJS(resultData201);
      return Promise.resolve<OutgoingWebhook>(result201);

    } else if (status === 400) {
      const _responseText = response.data;
      let result400: any = null;
      let resultData400 = _responseText;
      result400 = ProblemDetails.fromJS(resultData400);
      return throwException("A server side error occurred.", status, _responseText, _headers, result400);

    } else if (status === 403) {
      const _responseText = response.data;
      let result403: any = null;
      let resultData403 = _responseText;
      result403 = ProblemDetails.fromJS(resultData403);
      return throwException("A server side error occurred.", status, _responseText, _headers, result403);

    } else if (status === 409) {
      const _responseText = response.data;
      let result409: any = null;
      let resultData409 = _responseText;
      result409 = ProblemDetails.fromJS(resultData409);
      return throwException("A server side error occurred.", status, _responseText, _headers, result409);

    } else if (status !== 200 && status !== 204) {
      const _responseText = response.data;
      return throwException("An unexpected server error occurred.", status, _responseText, _headers);
    }
    return Promise.resolve<OutgoingWebhook>(null as any);
  }

  /**
   * Gets all webhooks for the current user.
   * @param isDeleted (optional) Whether to include non-deleted, deleted or all webhooks.
   * @param paginationToken (optional) The pagination token, optionally.
   * @param limit (optional) The max number of items to return, optionally.
   * @return An object with information about the webhook.
   */
  listWebhooks(isDeleted?: boolean | null | undefined, paginationToken?: string | null | undefined, limit?: number | null | undefined, cancelToken?: CancelToken): Promise<OutgoingWebhookPaginatedResult> {
    let url_ = this.baseUrl + "/api/v1/webhook?";
    if (isDeleted !== undefined && isDeleted !== null)
      url_ += "isDeleted=" + encodeURIComponent("" + isDeleted) + "&";
    if (paginationToken !== undefined && paginationToken !== null)
      url_ += "paginationToken=" + encodeURIComponent("" + paginationToken) + "&";
    if (limit !== undefined && limit !== null)
      url_ += "limit=" + encodeURIComponent("" + limit) + "&";
    url_ = url_.replace(/[?&]$/, "");

    let options_: AxiosRequestConfig = {
      method: "GET",
      url: url_,
      headers: {
        "Accept": "application/json"
      },
      cancelToken
    };

    return this.instance.request(options_).catch((_error: any) => {
      if (isAxiosError(_error) && _error.response) {
        return _error.response;
      } else {
        throw _error;
      }
    }).then((_response: AxiosResponse) => {
      return this.processListWebhooks(_response);
    });
  }

  protected processListWebhooks(response: AxiosResponse): Promise<OutgoingWebhookPaginatedResult> {
    const status = response.status;
    let _headers: any = {};
    if (response.headers && typeof response.headers === "object") {
      for (const k in response.headers) {
        if (response.headers.hasOwnProperty(k)) {
          _headers[k] = response.headers[k];
        }
      }
    }
    if (status === 200) {
      const _responseText = response.data;
      let result200: any = null;
      let resultData200 = _responseText;
      result200 = OutgoingWebhookPaginatedResult.fromJS(resultData200);
      return Promise.resolve<OutgoingWebhookPaginatedResult>(result200);

    } else if (status === 403) {
      const _responseText = response.data;
      let result403: any = null;
      let resultData403 = _responseText;
      result403 = ProblemDetails.fromJS(resultData403);
      return throwException("A server side error occurred.", status, _responseText, _headers, result403);

    } else if (status !== 200 && status !== 204) {
      const _responseText = response.data;
      return throwException("An unexpected server error occurred.", status, _responseText, _headers);
    }
    return Promise.resolve<OutgoingWebhookPaginatedResult>(null as any);
  }

  /**
   * Deletes the webhook with the given ID.
   * @return A status code indicating the result.
   */
  deleteUserWebhooks(cancelToken?: CancelToken): Promise<void> {
    let url_ = this.baseUrl + "/api/v1/webhook/batch";
    url_ = url_.replace(/[?&]$/, "");

    let options_: AxiosRequestConfig = {
      method: "DELETE",
      url: url_,
      headers: {},
      cancelToken
    };

    return this.instance.request(options_).catch((_error: any) => {
      if (isAxiosError(_error) && _error.response) {
        return _error.response;
      } else {
        throw _error;
      }
    }).then((_response: AxiosResponse) => {
      return this.processDeleteUserWebhooks(_response);
    });
  }

  protected processDeleteUserWebhooks(response: AxiosResponse): Promise<void> {
    const status = response.status;
    let _headers: any = {};
    if (response.headers && typeof response.headers === "object") {
      for (const k in response.headers) {
        if (response.headers.hasOwnProperty(k)) {
          _headers[k] = response.headers[k];
        }
      }
    }
    if (status === 204) {
      const _responseText = response.data;
      return Promise.resolve<void>(null as any);

    } else if (status === 403) {
      const _responseText = response.data;
      let result403: any = null;
      let resultData403 = _responseText;
      result403 = ProblemDetails.fromJS(resultData403);
      return throwException("A server side error occurred.", status, _responseText, _headers, result403);

    } else if (status !== 200 && status !== 204) {
      const _responseText = response.data;
      return throwException("An unexpected server error occurred.", status, _responseText, _headers);
    }
    return Promise.resolve<void>(null as any);
  }

  /**
   * Deletes the webhook with the given ID for the given user.
   * @param userId The user ID.
   * @return A status code indicating the result.
   */
  deleteUserWebhooksAdmin(userId: string, cancelToken?: CancelToken): Promise<void> {
    let url_ = this.baseUrl + "/api/v1/webhook/admin/batch/{userId}";
    if (userId === undefined || userId === null)
      throw new globalThis.Error("The parameter 'userId' must be defined.");
    url_ = url_.replace("{userId}", encodeURIComponent("" + userId));
    url_ = url_.replace(/[?&]$/, "");

    let options_: AxiosRequestConfig = {
      method: "DELETE",
      url: url_,
      headers: {},
      cancelToken
    };

    return this.instance.request(options_).catch((_error: any) => {
      if (isAxiosError(_error) && _error.response) {
        return _error.response;
      } else {
        throw _error;
      }
    }).then((_response: AxiosResponse) => {
      return this.processDeleteUserWebhooksAdmin(_response);
    });
  }

  protected processDeleteUserWebhooksAdmin(response: AxiosResponse): Promise<void> {
    const status = response.status;
    let _headers: any = {};
    if (response.headers && typeof response.headers === "object") {
      for (const k in response.headers) {
        if (response.headers.hasOwnProperty(k)) {
          _headers[k] = response.headers[k];
        }
      }
    }
    if (status === 204) {
      const _responseText = response.data;
      return Promise.resolve<void>(null as any);

    } else if (status === 400) {
      const _responseText = response.data;
      let result400: any = null;
      let resultData400 = _responseText;
      result400 = ProblemDetails.fromJS(resultData400);
      return throwException("A server side error occurred.", status, _responseText, _headers, result400);

    } else if (status !== 200 && status !== 204) {
      const _responseText = response.data;
      return throwException("An unexpected server error occurred.", status, _responseText, _headers);
    }
    return Promise.resolve<void>(null as any);
  }

  /**
   * Deletes the webhook with the given ID.
   * @param id The webhook ID.
   * @return A status code indicating the result.
   */
  deleteWebhook(id: string, cancelToken?: CancelToken): Promise<void> {
    let url_ = this.baseUrl + "/api/v1/webhook/{id}";
    if (id === undefined || id === null)
      throw new globalThis.Error("The parameter 'id' must be defined.");
    url_ = url_.replace("{id}", encodeURIComponent("" + id));
    url_ = url_.replace(/[?&]$/, "");

    let options_: AxiosRequestConfig = {
      method: "DELETE",
      url: url_,
      headers: {},
      cancelToken
    };

    return this.instance.request(options_).catch((_error: any) => {
      if (isAxiosError(_error) && _error.response) {
        return _error.response;
      } else {
        throw _error;
      }
    }).then((_response: AxiosResponse) => {
      return this.processDeleteWebhook(_response);
    });
  }

  protected processDeleteWebhook(response: AxiosResponse): Promise<void> {
    const status = response.status;
    let _headers: any = {};
    if (response.headers && typeof response.headers === "object") {
      for (const k in response.headers) {
        if (response.headers.hasOwnProperty(k)) {
          _headers[k] = response.headers[k];
        }
      }
    }
    if (status === 204) {
      const _responseText = response.data;
      return Promise.resolve<void>(null as any);

    } else if (status === 400) {
      const _responseText = response.data;
      let result400: any = null;
      let resultData400 = _responseText;
      result400 = ProblemDetails.fromJS(resultData400);
      return throwException("A server side error occurred.", status, _responseText, _headers, result400);

    } else if (status === 403) {
      const _responseText = response.data;
      let result403: any = null;
      let resultData403 = _responseText;
      result403 = ProblemDetails.fromJS(resultData403);
      return throwException("A server side error occurred.", status, _responseText, _headers, result403);

    } else if (status === 404) {
      const _responseText = response.data;
      let result404: any = null;
      let resultData404 = _responseText;
      result404 = ProblemDetails.fromJS(resultData404);
      return throwException("A server side error occurred.", status, _responseText, _headers, result404);

    } else if (status !== 200 && status !== 204) {
      const _responseText = response.data;
      return throwException("An unexpected server error occurred.", status, _responseText, _headers);
    }
    return Promise.resolve<void>(null as any);
  }

  /**
   * Edits a webhook according to the information in the DTO object.
   * @param id The webhook ID.
   * @param dto (optional) The DTO with the updated information.
   * @return An object with information about the webhook.
   */
  editWebhook(id: string, dto?: EditOutgoingWebhookRequest | null | undefined, cancelToken?: CancelToken): Promise<OutgoingWebhook> {
    let url_ = this.baseUrl + "/api/v1/webhook/{id}";
    if (id === undefined || id === null)
      throw new globalThis.Error("The parameter 'id' must be defined.");
    url_ = url_.replace("{id}", encodeURIComponent("" + id));
    url_ = url_.replace(/[?&]$/, "");

    const content_ = JSON.stringify(dto);

    let options_: AxiosRequestConfig = {
      data: content_,
      method: "PUT",
      url: url_,
      headers: {
        "Content-Type": "application/json",
        "Accept": "application/json"
      },
      cancelToken
    };

    return this.instance.request(options_).catch((_error: any) => {
      if (isAxiosError(_error) && _error.response) {
        return _error.response;
      } else {
        throw _error;
      }
    }).then((_response: AxiosResponse) => {
      return this.processEditWebhook(_response);
    });
  }

  protected processEditWebhook(response: AxiosResponse): Promise<OutgoingWebhook> {
    const status = response.status;
    let _headers: any = {};
    if (response.headers && typeof response.headers === "object") {
      for (const k in response.headers) {
        if (response.headers.hasOwnProperty(k)) {
          _headers[k] = response.headers[k];
        }
      }
    }
    if (status === 200) {
      const _responseText = response.data;
      let result200: any = null;
      let resultData200 = _responseText;
      result200 = OutgoingWebhook.fromJS(resultData200);
      return Promise.resolve<OutgoingWebhook>(result200);

    } else if (status === 400) {
      const _responseText = response.data;
      let result400: any = null;
      let resultData400 = _responseText;
      result400 = ProblemDetails.fromJS(resultData400);
      return throwException("A server side error occurred.", status, _responseText, _headers, result400);

    } else if (status === 403) {
      const _responseText = response.data;
      let result403: any = null;
      let resultData403 = _responseText;
      result403 = ProblemDetails.fromJS(resultData403);
      return throwException("A server side error occurred.", status, _responseText, _headers, result403);

    } else if (status === 409) {
      const _responseText = response.data;
      let result409: any = null;
      let resultData409 = _responseText;
      result409 = ProblemDetails.fromJS(resultData409);
      return throwException("A server side error occurred.", status, _responseText, _headers, result409);

    } else if (status !== 200 && status !== 204) {
      const _responseText = response.data;
      return throwException("An unexpected server error occurred.", status, _responseText, _headers);
    }
    return Promise.resolve<OutgoingWebhook>(null as any);
  }

  /**
   * Gets the webhook with the given ID.
   * @param id The webhook ID.
   * @return An object with information about the webhook.
   */
  getWebhook(id: string, cancelToken?: CancelToken): Promise<OutgoingWebhook> {
    let url_ = this.baseUrl + "/api/v1/webhook/{id}";
    if (id === undefined || id === null)
      throw new globalThis.Error("The parameter 'id' must be defined.");
    url_ = url_.replace("{id}", encodeURIComponent("" + id));
    url_ = url_.replace(/[?&]$/, "");

    let options_: AxiosRequestConfig = {
      method: "GET",
      url: url_,
      headers: {
        "Accept": "application/json"
      },
      cancelToken
    };

    return this.instance.request(options_).catch((_error: any) => {
      if (isAxiosError(_error) && _error.response) {
        return _error.response;
      } else {
        throw _error;
      }
    }).then((_response: AxiosResponse) => {
      return this.processGetWebhook(_response);
    });
  }

  protected processGetWebhook(response: AxiosResponse): Promise<OutgoingWebhook> {
    const status = response.status;
    let _headers: any = {};
    if (response.headers && typeof response.headers === "object") {
      for (const k in response.headers) {
        if (response.headers.hasOwnProperty(k)) {
          _headers[k] = response.headers[k];
        }
      }
    }
    if (status === 200) {
      const _responseText = response.data;
      let result200: any = null;
      let resultData200 = _responseText;
      result200 = OutgoingWebhook.fromJS(resultData200);
      return Promise.resolve<OutgoingWebhook>(result200);

    } else if (status === 403) {
      const _responseText = response.data;
      let result403: any = null;
      let resultData403 = _responseText;
      result403 = ProblemDetails.fromJS(resultData403);
      return throwException("A server side error occurred.", status, _responseText, _headers, result403);

    } else if (status === 404) {
      const _responseText = response.data;
      let result404: any = null;
      let resultData404 = _responseText;
      result404 = ProblemDetails.fromJS(resultData404);
      return throwException("A server side error occurred.", status, _responseText, _headers, result404);

    } else if (status !== 200 && status !== 204) {
      const _responseText = response.data;
      return throwException("An unexpected server error occurred.", status, _responseText, _headers);
    }
    return Promise.resolve<OutgoingWebhook>(null as any);
  }

  /**
   * Deletes the webhook with the given ID for the given user.
   * @param userId The user ID.
   * @param id The webhook ID.
   * @return A status code indicating the result.
   */
  deleteWebhookAdmin(userId: string, id: string, cancelToken?: CancelToken): Promise<void> {
    let url_ = this.baseUrl + "/api/v1/webhook/admin/{userId}/{id}";
    if (userId === undefined || userId === null)
      throw new globalThis.Error("The parameter 'userId' must be defined.");
    url_ = url_.replace("{userId}", encodeURIComponent("" + userId));
    if (id === undefined || id === null)
      throw new globalThis.Error("The parameter 'id' must be defined.");
    url_ = url_.replace("{id}", encodeURIComponent("" + id));
    url_ = url_.replace(/[?&]$/, "");

    let options_: AxiosRequestConfig = {
      method: "DELETE",
      url: url_,
      headers: {},
      cancelToken
    };

    return this.instance.request(options_).catch((_error: any) => {
      if (isAxiosError(_error) && _error.response) {
        return _error.response;
      } else {
        throw _error;
      }
    }).then((_response: AxiosResponse) => {
      return this.processDeleteWebhookAdmin(_response);
    });
  }

  protected processDeleteWebhookAdmin(response: AxiosResponse): Promise<void> {
    const status = response.status;
    let _headers: any = {};
    if (response.headers && typeof response.headers === "object") {
      for (const k in response.headers) {
        if (response.headers.hasOwnProperty(k)) {
          _headers[k] = response.headers[k];
        }
      }
    }
    if (status === 204) {
      const _responseText = response.data;
      return Promise.resolve<void>(null as any);

    } else if (status === 400) {
      const _responseText = response.data;
      let result400: any = null;
      let resultData400 = _responseText;
      result400 = ProblemDetails.fromJS(resultData400);
      return throwException("A server side error occurred.", status, _responseText, _headers, result400);

    } else if (status === 404) {
      const _responseText = response.data;
      let result404: any = null;
      let resultData404 = _responseText;
      result404 = ProblemDetails.fromJS(resultData404);
      return throwException("A server side error occurred.", status, _responseText, _headers, result404);

    } else if (status !== 200 && status !== 204) {
      const _responseText = response.data;
      return throwException("An unexpected server error occurred.", status, _responseText, _headers);
    }
    return Promise.resolve<void>(null as any);
  }

  /**
   * Edits a webhook according to the information in the DTO object.
   * @param userId The user ID.
   * @param id The webhook ID.
   * @param dto (optional) The DTO with the updated information.
   * @return An object with information about the webhook.
   */
  editWebhookAdmin(userId: string, id: string, dto?: EditOutgoingWebhookRequest | null | undefined, cancelToken?: CancelToken): Promise<OutgoingWebhook> {
    let url_ = this.baseUrl + "/api/v1/webhook/admin/{userId}/{id}";
    if (userId === undefined || userId === null)
      throw new globalThis.Error("The parameter 'userId' must be defined.");
    url_ = url_.replace("{userId}", encodeURIComponent("" + userId));
    if (id === undefined || id === null)
      throw new globalThis.Error("The parameter 'id' must be defined.");
    url_ = url_.replace("{id}", encodeURIComponent("" + id));
    url_ = url_.replace(/[?&]$/, "");

    const content_ = JSON.stringify(dto);

    let options_: AxiosRequestConfig = {
      data: content_,
      method: "PUT",
      url: url_,
      headers: {
        "Content-Type": "application/json",
        "Accept": "application/json"
      },
      cancelToken
    };

    return this.instance.request(options_).catch((_error: any) => {
      if (isAxiosError(_error) && _error.response) {
        return _error.response;
      } else {
        throw _error;
      }
    }).then((_response: AxiosResponse) => {
      return this.processEditWebhookAdmin(_response);
    });
  }

  protected processEditWebhookAdmin(response: AxiosResponse): Promise<OutgoingWebhook> {
    const status = response.status;
    let _headers: any = {};
    if (response.headers && typeof response.headers === "object") {
      for (const k in response.headers) {
        if (response.headers.hasOwnProperty(k)) {
          _headers[k] = response.headers[k];
        }
      }
    }
    if (status === 200) {
      const _responseText = response.data;
      let result200: any = null;
      let resultData200 = _responseText;
      result200 = OutgoingWebhook.fromJS(resultData200);
      return Promise.resolve<OutgoingWebhook>(result200);

    } else if (status === 400) {
      const _responseText = response.data;
      let result400: any = null;
      let resultData400 = _responseText;
      result400 = ProblemDetails.fromJS(resultData400);
      return throwException("A server side error occurred.", status, _responseText, _headers, result400);

    } else if (status === 403) {
      const _responseText = response.data;
      let result403: any = null;
      let resultData403 = _responseText;
      result403 = ProblemDetails.fromJS(resultData403);
      return throwException("A server side error occurred.", status, _responseText, _headers, result403);

    } else if (status === 409) {
      const _responseText = response.data;
      let result409: any = null;
      let resultData409 = _responseText;
      result409 = ProblemDetails.fromJS(resultData409);
      return throwException("A server side error occurred.", status, _responseText, _headers, result409);

    } else if (status !== 200 && status !== 204) {
      const _responseText = response.data;
      return throwException("An unexpected server error occurred.", status, _responseText, _headers);
    }
    return Promise.resolve<OutgoingWebhook>(null as any);
  }

  /**
   * Gets the webhook with the given ID.
   * @param id The webhook ID.
   * @return An object with information about the webhook.
   */
  getWebhookSecret(id: string, cancelToken?: CancelToken): Promise<OutgoingWebhookSecret> {
    let url_ = this.baseUrl + "/api/v1/webhook/secret/{id}";
    if (id === undefined || id === null)
      throw new globalThis.Error("The parameter 'id' must be defined.");
    url_ = url_.replace("{id}", encodeURIComponent("" + id));
    url_ = url_.replace(/[?&]$/, "");

    let options_: AxiosRequestConfig = {
      method: "GET",
      url: url_,
      headers: {
        "Accept": "application/json"
      },
      cancelToken
    };

    return this.instance.request(options_).catch((_error: any) => {
      if (isAxiosError(_error) && _error.response) {
        return _error.response;
      } else {
        throw _error;
      }
    }).then((_response: AxiosResponse) => {
      return this.processGetWebhookSecret(_response);
    });
  }

  protected processGetWebhookSecret(response: AxiosResponse): Promise<OutgoingWebhookSecret> {
    const status = response.status;
    let _headers: any = {};
    if (response.headers && typeof response.headers === "object") {
      for (const k in response.headers) {
        if (response.headers.hasOwnProperty(k)) {
          _headers[k] = response.headers[k];
        }
      }
    }
    if (status === 200) {
      const _responseText = response.data;
      let result200: any = null;
      let resultData200 = _responseText;
      result200 = OutgoingWebhookSecret.fromJS(resultData200);
      return Promise.resolve<OutgoingWebhookSecret>(result200);

    } else if (status === 403) {
      const _responseText = response.data;
      let result403: any = null;
      let resultData403 = _responseText;
      result403 = ProblemDetails.fromJS(resultData403);
      return throwException("A server side error occurred.", status, _responseText, _headers, result403);

    } else if (status === 404) {
      const _responseText = response.data;
      let result404: any = null;
      let resultData404 = _responseText;
      result404 = ProblemDetails.fromJS(resultData404);
      return throwException("A server side error occurred.", status, _responseText, _headers, result404);

    } else if (status !== 200 && status !== 204) {
      const _responseText = response.data;
      return throwException("An unexpected server error occurred.", status, _responseText, _headers);
    }
    return Promise.resolve<OutgoingWebhookSecret>(null as any);
  }

  /**
   * Gets all webhooks for the current user.
   * @param isDeleted (optional) Whether to include non-deleted, deleted or all webhooks.
   * @param paginationToken (optional) The pagination token, optionally.
   * @param limit (optional) The max number of items to return, optionally.
   * @return An object with information about the webhook.
   */
  listUserWebhookHistories(isDeleted?: boolean | null | undefined, paginationToken?: string | null | undefined, limit?: number | null | undefined, cancelToken?: CancelToken): Promise<OutgoingWebhookEventPaginatedResult> {
    let url_ = this.baseUrl + "/api/v1/webhook/history?";
    if (isDeleted !== undefined && isDeleted !== null)
      url_ += "isDeleted=" + encodeURIComponent("" + isDeleted) + "&";
    if (paginationToken !== undefined && paginationToken !== null)
      url_ += "paginationToken=" + encodeURIComponent("" + paginationToken) + "&";
    if (limit !== undefined && limit !== null)
      url_ += "limit=" + encodeURIComponent("" + limit) + "&";
    url_ = url_.replace(/[?&]$/, "");

    let options_: AxiosRequestConfig = {
      method: "GET",
      url: url_,
      headers: {
        "Accept": "application/json"
      },
      cancelToken
    };

    return this.instance.request(options_).catch((_error: any) => {
      if (isAxiosError(_error) && _error.response) {
        return _error.response;
      } else {
        throw _error;
      }
    }).then((_response: AxiosResponse) => {
      return this.processListUserWebhookHistories(_response);
    });
  }

  protected processListUserWebhookHistories(response: AxiosResponse): Promise<OutgoingWebhookEventPaginatedResult> {
    const status = response.status;
    let _headers: any = {};
    if (response.headers && typeof response.headers === "object") {
      for (const k in response.headers) {
        if (response.headers.hasOwnProperty(k)) {
          _headers[k] = response.headers[k];
        }
      }
    }
    if (status === 200) {
      const _responseText = response.data;
      let result200: any = null;
      let resultData200 = _responseText;
      result200 = OutgoingWebhookEventPaginatedResult.fromJS(resultData200);
      return Promise.resolve<OutgoingWebhookEventPaginatedResult>(result200);

    } else if (status === 403) {
      const _responseText = response.data;
      let result403: any = null;
      let resultData403 = _responseText;
      result403 = ProblemDetails.fromJS(resultData403);
      return throwException("A server side error occurred.", status, _responseText, _headers, result403);

    } else if (status !== 200 && status !== 204) {
      const _responseText = response.data;
      return throwException("An unexpected server error occurred.", status, _responseText, _headers);
    }
    return Promise.resolve<OutgoingWebhookEventPaginatedResult>(null as any);
  }

  /**
   * Gets all history for a single webhook.
   * @param id The webhook ID.
   * @param paginationToken (optional) The pagination token, optionally.
   * @param limit (optional) The max number of items to return, optionally.
   * @return An object with information about the webhook.
   */
  listWebhookHistory(id: string, paginationToken?: string | null | undefined, limit?: number | null | undefined, cancelToken?: CancelToken): Promise<OutgoingWebhookEventPaginatedResult> {
    let url_ = this.baseUrl + "/api/v1/webhook/history/{id}?";
    if (id === undefined || id === null)
      throw new globalThis.Error("The parameter 'id' must be defined.");
    url_ = url_.replace("{id}", encodeURIComponent("" + id));
    if (paginationToken !== undefined && paginationToken !== null)
      url_ += "paginationToken=" + encodeURIComponent("" + paginationToken) + "&";
    if (limit !== undefined && limit !== null)
      url_ += "limit=" + encodeURIComponent("" + limit) + "&";
    url_ = url_.replace(/[?&]$/, "");

    let options_: AxiosRequestConfig = {
      method: "GET",
      url: url_,
      headers: {
        "Accept": "application/json"
      },
      cancelToken
    };

    return this.instance.request(options_).catch((_error: any) => {
      if (isAxiosError(_error) && _error.response) {
        return _error.response;
      } else {
        throw _error;
      }
    }).then((_response: AxiosResponse) => {
      return this.processListWebhookHistory(_response);
    });
  }

  protected processListWebhookHistory(response: AxiosResponse): Promise<OutgoingWebhookEventPaginatedResult> {
    const status = response.status;
    let _headers: any = {};
    if (response.headers && typeof response.headers === "object") {
      for (const k in response.headers) {
        if (response.headers.hasOwnProperty(k)) {
          _headers[k] = response.headers[k];
        }
      }
    }
    if (status === 200) {
      const _responseText = response.data;
      let result200: any = null;
      let resultData200 = _responseText;
      result200 = OutgoingWebhookEventPaginatedResult.fromJS(resultData200);
      return Promise.resolve<OutgoingWebhookEventPaginatedResult>(result200);

    } else if (status === 403) {
      const _responseText = response.data;
      let result403: any = null;
      let resultData403 = _responseText;
      result403 = ProblemDetails.fromJS(resultData403);
      return throwException("A server side error occurred.", status, _responseText, _headers, result403);

    } else if (status !== 200 && status !== 204) {
      const _responseText = response.data;
      return throwException("An unexpected server error occurred.", status, _responseText, _headers);
    }
    return Promise.resolve<OutgoingWebhookEventPaginatedResult>(null as any);
  }

  /**
   * Gets all webhooks for the current user.
   * @param userId The user ID for which to retrieve the webhooks.
   * @param isDeleted (optional) Whether to include non-deleted, deleted or all webhooks.
   * @param paginationToken (optional) The pagination token, optionally.
   * @param limit (optional) The max number of items to return, optionally.
   * @return An object with information about the webhook.
   */
  listWebhooksAdmin(userId: string, isDeleted?: boolean | null | undefined, paginationToken?: string | null | undefined, limit?: number | null | undefined, cancelToken?: CancelToken): Promise<OutgoingWebhookPaginatedResult> {
    let url_ = this.baseUrl + "/api/v1/webhook/admin/{userId}?";
    if (userId === undefined || userId === null)
      throw new globalThis.Error("The parameter 'userId' must be defined.");
    url_ = url_.replace("{userId}", encodeURIComponent("" + userId));
    if (isDeleted !== undefined && isDeleted !== null)
      url_ += "isDeleted=" + encodeURIComponent("" + isDeleted) + "&";
    if (paginationToken !== undefined && paginationToken !== null)
      url_ += "paginationToken=" + encodeURIComponent("" + paginationToken) + "&";
    if (limit !== undefined && limit !== null)
      url_ += "limit=" + encodeURIComponent("" + limit) + "&";
    url_ = url_.replace(/[?&]$/, "");

    let options_: AxiosRequestConfig = {
      method: "GET",
      url: url_,
      headers: {
        "Accept": "application/json"
      },
      cancelToken
    };

    return this.instance.request(options_).catch((_error: any) => {
      if (isAxiosError(_error) && _error.response) {
        return _error.response;
      } else {
        throw _error;
      }
    }).then((_response: AxiosResponse) => {
      return this.processListWebhooksAdmin(_response);
    });
  }

  protected processListWebhooksAdmin(response: AxiosResponse): Promise<OutgoingWebhookPaginatedResult> {
    const status = response.status;
    let _headers: any = {};
    if (response.headers && typeof response.headers === "object") {
      for (const k in response.headers) {
        if (response.headers.hasOwnProperty(k)) {
          _headers[k] = response.headers[k];
        }
      }
    }
    if (status === 200) {
      const _responseText = response.data;
      let result200: any = null;
      let resultData200 = _responseText;
      result200 = OutgoingWebhookPaginatedResult.fromJS(resultData200);
      return Promise.resolve<OutgoingWebhookPaginatedResult>(result200);

    } else if (status === 400) {
      const _responseText = response.data;
      let result400: any = null;
      let resultData400 = _responseText;
      result400 = ProblemDetails.fromJS(resultData400);
      return throwException("A server side error occurred.", status, _responseText, _headers, result400);

    } else if (status !== 200 && status !== 204) {
      const _responseText = response.data;
      return throwException("An unexpected server error occurred.", status, _responseText, _headers);
    }
    return Promise.resolve<OutgoingWebhookPaginatedResult>(null as any);
  }

  /**
   * Previews a webhook according to the information in the DTO object.
   * @param id The webhook ID.
   * @param dto (optional) The DTO with the updated information, optionally.
   * @return An object with information about the webhook.
   */
  previewExistingWebhookBody(id: string, dto?: EditOutgoingWebhookRequest | null | undefined, cancelToken?: CancelToken): Promise<OutgoingWebhookBody> {
    let url_ = this.baseUrl + "/api/v1/webhook/preview/{id}";
    if (id === undefined || id === null)
      throw new globalThis.Error("The parameter 'id' must be defined.");
    url_ = url_.replace("{id}", encodeURIComponent("" + id));
    url_ = url_.replace(/[?&]$/, "");

    const content_ = JSON.stringify(dto);

    let options_: AxiosRequestConfig = {
      data: content_,
      method: "POST",
      url: url_,
      headers: {
        "Content-Type": "application/json",
        "Accept": "application/json"
      },
      cancelToken
    };

    return this.instance.request(options_).catch((_error: any) => {
      if (isAxiosError(_error) && _error.response) {
        return _error.response;
      } else {
        throw _error;
      }
    }).then((_response: AxiosResponse) => {
      return this.processPreviewExistingWebhookBody(_response);
    });
  }

  protected processPreviewExistingWebhookBody(response: AxiosResponse): Promise<OutgoingWebhookBody> {
    const status = response.status;
    let _headers: any = {};
    if (response.headers && typeof response.headers === "object") {
      for (const k in response.headers) {
        if (response.headers.hasOwnProperty(k)) {
          _headers[k] = response.headers[k];
        }
      }
    }
    if (status === 200) {
      const _responseText = response.data;
      let result200: any = null;
      let resultData200 = _responseText;
      result200 = OutgoingWebhookBody.fromJS(resultData200);
      return Promise.resolve<OutgoingWebhookBody>(result200);

    } else if (status === 400) {
      const _responseText = response.data;
      let result400: any = null;
      let resultData400 = _responseText;
      result400 = ProblemDetails.fromJS(resultData400);
      return throwException("A server side error occurred.", status, _responseText, _headers, result400);

    } else if (status !== 200 && status !== 204) {
      const _responseText = response.data;
      return throwException("An unexpected server error occurred.", status, _responseText, _headers);
    }
    return Promise.resolve<OutgoingWebhookBody>(null as any);
  }

  /**
   * Previews a webhook according to the information in the DTO object.
   * @param createWebhookDto (optional) The object defining the parameters of the webhook.
   * @return An object with information about the webhook.
   */
  previewWebhookBody(createWebhookDto?: CreateOutgoingWebhookRequest | null | undefined, cancelToken?: CancelToken): Promise<OutgoingWebhookBody> {
    let url_ = this.baseUrl + "/api/v1/webhook/preview";
    url_ = url_.replace(/[?&]$/, "");

    const content_ = JSON.stringify(createWebhookDto);

    let options_: AxiosRequestConfig = {
      data: content_,
      method: "POST",
      url: url_,
      headers: {
        "Content-Type": "application/json",
        "Accept": "application/json"
      },
      cancelToken
    };

    return this.instance.request(options_).catch((_error: any) => {
      if (isAxiosError(_error) && _error.response) {
        return _error.response;
      } else {
        throw _error;
      }
    }).then((_response: AxiosResponse) => {
      return this.processPreviewWebhookBody(_response);
    });
  }

  protected processPreviewWebhookBody(response: AxiosResponse): Promise<OutgoingWebhookBody> {
    const status = response.status;
    let _headers: any = {};
    if (response.headers && typeof response.headers === "object") {
      for (const k in response.headers) {
        if (response.headers.hasOwnProperty(k)) {
          _headers[k] = response.headers[k];
        }
      }
    }
    if (status === 200) {
      const _responseText = response.data;
      let result200: any = null;
      let resultData200 = _responseText;
      result200 = OutgoingWebhookBody.fromJS(resultData200);
      return Promise.resolve<OutgoingWebhookBody>(result200);

    } else if (status === 400) {
      const _responseText = response.data;
      let result400: any = null;
      let resultData400 = _responseText;
      result400 = ProblemDetails.fromJS(resultData400);
      return throwException("A server side error occurred.", status, _responseText, _headers, result400);

    } else if (status !== 200 && status !== 204) {
      const _responseText = response.data;
      return throwException("An unexpected server error occurred.", status, _responseText, _headers);
    }
    return Promise.resolve<OutgoingWebhookBody>(null as any);
  }

  /**
   * Tests an existing webhook to preview its response. Optionally, include information in the edit DTO to preview the
   response before saving.
   * @param id The webhook ID.
   * @param dto (optional) The DTO with the updated information, optionally.
   * @return An object with information about the webhook.
   */
  testExistingWebhook(id: string, dto?: EditOutgoingWebhookRequest | null | undefined, cancelToken?: CancelToken): Promise<OutgoingWebhookSendAttempt> {
    let url_ = this.baseUrl + "/api/v1/webhook/test/{id}";
    if (id === undefined || id === null)
      throw new globalThis.Error("The parameter 'id' must be defined.");
    url_ = url_.replace("{id}", encodeURIComponent("" + id));
    url_ = url_.replace(/[?&]$/, "");

    const content_ = JSON.stringify(dto);

    let options_: AxiosRequestConfig = {
      data: content_,
      method: "POST",
      url: url_,
      headers: {
        "Content-Type": "application/json",
        "Accept": "application/json"
      },
      cancelToken
    };

    return this.instance.request(options_).catch((_error: any) => {
      if (isAxiosError(_error) && _error.response) {
        return _error.response;
      } else {
        throw _error;
      }
    }).then((_response: AxiosResponse) => {
      return this.processTestExistingWebhook(_response);
    });
  }

  protected processTestExistingWebhook(response: AxiosResponse): Promise<OutgoingWebhookSendAttempt> {
    const status = response.status;
    let _headers: any = {};
    if (response.headers && typeof response.headers === "object") {
      for (const k in response.headers) {
        if (response.headers.hasOwnProperty(k)) {
          _headers[k] = response.headers[k];
        }
      }
    }
    if (status === 200) {
      const _responseText = response.data;
      let result200: any = null;
      let resultData200 = _responseText;
      result200 = OutgoingWebhookSendAttempt.fromJS(resultData200);
      return Promise.resolve<OutgoingWebhookSendAttempt>(result200);

    } else if (status === 400) {
      const _responseText = response.data;
      let result400: any = null;
      let resultData400 = _responseText;
      result400 = ProblemDetails.fromJS(resultData400);
      return throwException("A server side error occurred.", status, _responseText, _headers, result400);

    } else if (status === 403) {
      const _responseText = response.data;
      let result403: any = null;
      let resultData403 = _responseText;
      result403 = ProblemDetails.fromJS(resultData403);
      return throwException("A server side error occurred.", status, _responseText, _headers, result403);

    } else if (status === 409) {
      const _responseText = response.data;
      let result409: any = null;
      let resultData409 = _responseText;
      result409 = ProblemDetails.fromJS(resultData409);
      return throwException("A server side error occurred.", status, _responseText, _headers, result409);

    } else if (status !== 200 && status !== 204) {
      const _responseText = response.data;
      return throwException("An unexpected server error occurred.", status, _responseText, _headers);
    }
    return Promise.resolve<OutgoingWebhookSendAttempt>(null as any);
  }

  /**
   * Tests a new webhook before it has been saved.
   * @param createWebhookDto (optional) The object defining the parameters of the webhook.
   * @return An object with information about the webhook.
   */
  testNewWebhook(createWebhookDto?: CreateOutgoingWebhookRequest | null | undefined, cancelToken?: CancelToken): Promise<OutgoingWebhookSendAttempt> {
    let url_ = this.baseUrl + "/api/v1/webhook/test";
    url_ = url_.replace(/[?&]$/, "");

    const content_ = JSON.stringify(createWebhookDto);

    let options_: AxiosRequestConfig = {
      data: content_,
      method: "POST",
      url: url_,
      headers: {
        "Content-Type": "application/json",
        "Accept": "application/json"
      },
      cancelToken
    };

    return this.instance.request(options_).catch((_error: any) => {
      if (isAxiosError(_error) && _error.response) {
        return _error.response;
      } else {
        throw _error;
      }
    }).then((_response: AxiosResponse) => {
      return this.processTestNewWebhook(_response);
    });
  }

  protected processTestNewWebhook(response: AxiosResponse): Promise<OutgoingWebhookSendAttempt> {
    const status = response.status;
    let _headers: any = {};
    if (response.headers && typeof response.headers === "object") {
      for (const k in response.headers) {
        if (response.headers.hasOwnProperty(k)) {
          _headers[k] = response.headers[k];
        }
      }
    }
    if (status === 200) {
      const _responseText = response.data;
      let result200: any = null;
      let resultData200 = _responseText;
      result200 = OutgoingWebhookSendAttempt.fromJS(resultData200);
      return Promise.resolve<OutgoingWebhookSendAttempt>(result200);

    } else if (status === 400) {
      const _responseText = response.data;
      let result400: any = null;
      let resultData400 = _responseText;
      result400 = ProblemDetails.fromJS(resultData400);
      return throwException("A server side error occurred.", status, _responseText, _headers, result400);

    } else if (status === 403) {
      const _responseText = response.data;
      let result403: any = null;
      let resultData403 = _responseText;
      result403 = ProblemDetails.fromJS(resultData403);
      return throwException("A server side error occurred.", status, _responseText, _headers, result403);

    } else if (status === 409) {
      const _responseText = response.data;
      let result409: any = null;
      let resultData409 = _responseText;
      result409 = ProblemDetails.fromJS(resultData409);
      return throwException("A server side error occurred.", status, _responseText, _headers, result409);

    } else if (status !== 200 && status !== 204) {
      const _responseText = response.data;
      return throwException("An unexpected server error occurred.", status, _responseText, _headers);
    }
    return Promise.resolve<OutgoingWebhookSendAttempt>(null as any);
  }

  /**
   * Lists the contents of a ZIP file.
   * @param projectId The project id.
   * @param fileId The file id.
   * @param storageType (optional) The storage type. Defaults to Forge.
   * @param allowDeleted (optional) Whether to list a deleted file's contents.
   * @return The list of files contained in the ZIP.
   */
  listZipContents(projectId: string, fileId: string, storageType?: StorageType | undefined, allowDeleted?: boolean | undefined, cancelToken?: CancelToken): Promise<ZipEntryDto[]> {
    let url_ = this.baseUrl + "/api/v1/zip/{projectId}/{fileId}?";
    if (projectId === undefined || projectId === null)
      throw new globalThis.Error("The parameter 'projectId' must be defined.");
    url_ = url_.replace("{projectId}", encodeURIComponent("" + projectId));
    if (fileId === undefined || fileId === null)
      throw new globalThis.Error("The parameter 'fileId' must be defined.");
    url_ = url_.replace("{fileId}", encodeURIComponent("" + fileId));
    if (storageType === null)
      throw new globalThis.Error("The parameter 'storageType' cannot be null.");
    else if (storageType !== undefined)
      url_ += "storageType=" + encodeURIComponent("" + storageType) + "&";
    if (allowDeleted === null)
      throw new globalThis.Error("The parameter 'allowDeleted' cannot be null.");
    else if (allowDeleted !== undefined)
      url_ += "allowDeleted=" + encodeURIComponent("" + allowDeleted) + "&";
    url_ = url_.replace(/[?&]$/, "");

    let options_: AxiosRequestConfig = {
      method: "GET",
      url: url_,
      headers: {
        "Accept": "application/json"
      },
      cancelToken
    };

    return this.instance.request(options_).catch((_error: any) => {
      if (isAxiosError(_error) && _error.response) {
        return _error.response;
      } else {
        throw _error;
      }
    }).then((_response: AxiosResponse) => {
      return this.processListZipContents(_response);
    });
  }

  protected processListZipContents(response: AxiosResponse): Promise<ZipEntryDto[]> {
    const status = response.status;
    let _headers: any = {};
    if (response.headers && typeof response.headers === "object") {
      for (const k in response.headers) {
        if (response.headers.hasOwnProperty(k)) {
          _headers[k] = response.headers[k];
        }
      }
    }
    if (status === 200) {
      const _responseText = response.data;
      let result200: any = null;
      let resultData200 = _responseText;
      if (Array.isArray(resultData200)) {
        result200 = [] as any;
        for (let item of resultData200)
          result200!.push(ZipEntryDto.fromJS(item));
      } else {
        result200 = null as any;
      }
      return Promise.resolve<ZipEntryDto[]>(result200);

    } else if (status === 400) {
      const _responseText = response.data;
      let result400: any = null;
      let resultData400 = _responseText;
      result400 = ProblemDetails.fromJS(resultData400);
      return throwException("A server side error occurred.", status, _responseText, _headers, result400);

    } else if (status !== 200 && status !== 204) {
      const _responseText = response.data;
      return throwException("An unexpected server error occurred.", status, _responseText, _headers);
    }
    return Promise.resolve<ZipEntryDto[]>(null as any);
  }

  /**
   * Extracts the contents of a ZIP file.
   * @param extractRequest (optional) The parameters for the extraction job.
   * @return A list of status objects for the job.
   */
  startExtract(extractRequest?: StartExtractRequest | null | undefined, cancelToken?: CancelToken): Promise<StartExtractResponse[]> {
    let url_ = this.baseUrl + "/api/v1/zip";
    url_ = url_.replace(/[?&]$/, "");

    const content_ = JSON.stringify(extractRequest);

    let options_: AxiosRequestConfig = {
      data: content_,
      method: "POST",
      url: url_,
      headers: {
        "Content-Type": "application/json",
        "Accept": "application/json"
      },
      cancelToken
    };

    return this.instance.request(options_).catch((_error: any) => {
      if (isAxiosError(_error) && _error.response) {
        return _error.response;
      } else {
        throw _error;
      }
    }).then((_response: AxiosResponse) => {
      return this.processStartExtract(_response);
    });
  }

  protected processStartExtract(response: AxiosResponse): Promise<StartExtractResponse[]> {
    const status = response.status;
    let _headers: any = {};
    if (response.headers && typeof response.headers === "object") {
      for (const k in response.headers) {
        if (response.headers.hasOwnProperty(k)) {
          _headers[k] = response.headers[k];
        }
      }
    }
    if (status === 200) {
      const _responseText = response.data;
      let result200: any = null;
      let resultData200 = _responseText;
      if (Array.isArray(resultData200)) {
        result200 = [] as any;
        for (let item of resultData200)
          result200!.push(StartExtractResponse.fromJS(item));
      } else {
        result200 = null as any;
      }
      return Promise.resolve<StartExtractResponse[]>(result200);

    } else if (status === 400) {
      const _responseText = response.data;
      let result400: any = null;
      let resultData400 = _responseText;
      result400 = ProblemDetails.fromJS(resultData400);
      return throwException("A server side error occurred.", status, _responseText, _headers, result400);

    } else if (status !== 200 && status !== 204) {
      const _responseText = response.data;
      return throwException("An unexpected server error occurred.", status, _responseText, _headers);
    }
    return Promise.resolve<StartExtractResponse[]>(null as any);
  }

  /**
   * Gets the status of an extraction job.
   * @param id The job ID.
   * @return The current status of the job.
   */
  status(id: string, cancelToken?: CancelToken): Promise<FileStatus> {
    let url_ = this.baseUrl + "/api/v1/zip/status/{id}";
    if (id === undefined || id === null)
      throw new globalThis.Error("The parameter 'id' must be defined.");
    url_ = url_.replace("{id}", encodeURIComponent("" + id));
    url_ = url_.replace(/[?&]$/, "");

    let options_: AxiosRequestConfig = {
      method: "GET",
      url: url_,
      headers: {
        "Accept": "application/json"
      },
      cancelToken
    };

    return this.instance.request(options_).catch((_error: any) => {
      if (isAxiosError(_error) && _error.response) {
        return _error.response;
      } else {
        throw _error;
      }
    }).then((_response: AxiosResponse) => {
      return this.processStatus(_response);
    });
  }

  protected processStatus(response: AxiosResponse): Promise<FileStatus> {
    const status = response.status;
    let _headers: any = {};
    if (response.headers && typeof response.headers === "object") {
      for (const k in response.headers) {
        if (response.headers.hasOwnProperty(k)) {
          _headers[k] = response.headers[k];
        }
      }
    }
    if (status === 200) {
      const _responseText = response.data;
      let result200: any = null;
      let resultData200 = _responseText;
      result200 = FileStatus.fromJS(resultData200);
      return Promise.resolve<FileStatus>(result200);

    } else if (status === 400) {
      const _responseText = response.data;
      let result400: any = null;
      let resultData400 = _responseText;
      result400 = ProblemDetails.fromJS(resultData400);
      return throwException("A server side error occurred.", status, _responseText, _headers, result400);

    } else if (status !== 200 && status !== 204) {
      const _responseText = response.data;
      return throwException("An unexpected server error occurred.", status, _responseText, _headers);
    }
    return Promise.resolve<FileStatus>(null as any);
  }

  /**
   * Creates a customer.
   * @param dto (optional) A DTO containing the customer information.
   * @return The created customer.
   */
  createCustomer(dto?: CreateCustomerRequest | null | undefined, cancelToken?: CancelToken): Promise<CustomerInfo> {
    let url_ = this.baseUrl + "/api/v1/customer";
    url_ = url_.replace(/[?&]$/, "");

    const content_ = JSON.stringify(dto);

    let options_: AxiosRequestConfig = {
      data: content_,
      method: "POST",
      url: url_,
      headers: {
        "Content-Type": "application/json",
        "Accept": "application/json"
      },
      cancelToken
    };

    return this.instance.request(options_).catch((_error: any) => {
      if (isAxiosError(_error) && _error.response) {
        return _error.response;
      } else {
        throw _error;
      }
    }).then((_response: AxiosResponse) => {
      return this.processCreateCustomer(_response);
    });
  }

  protected processCreateCustomer(response: AxiosResponse): Promise<CustomerInfo> {
    const status = response.status;
    let _headers: any = {};
    if (response.headers && typeof response.headers === "object") {
      for (const k in response.headers) {
        if (response.headers.hasOwnProperty(k)) {
          _headers[k] = response.headers[k];
        }
      }
    }
    if (status === 201) {
      const _responseText = response.data;
      let result201: any = null;
      let resultData201 = _responseText;
      result201 = CustomerInfo.fromJS(resultData201);
      return Promise.resolve<CustomerInfo>(result201);

    } else if (status === 400) {
      const _responseText = response.data;
      let result400: any = null;
      let resultData400 = _responseText;
      result400 = ProblemDetails.fromJS(resultData400);
      return throwException("A server side error occurred.", status, _responseText, _headers, result400);

    } else if (status !== 200 && status !== 204) {
      const _responseText = response.data;
      return throwException("An unexpected server error occurred.", status, _responseText, _headers);
    }
    return Promise.resolve<CustomerInfo>(null as any);
  }

  /**
   * Lists all customers.
   * @return All customers.
   * @deprecated
   */
  listCustomers(cancelToken?: CancelToken): Promise<CustomerInfo[]> {
    let url_ = this.baseUrl + "/api/v1/customer";
    url_ = url_.replace(/[?&]$/, "");

    let options_: AxiosRequestConfig = {
      method: "GET",
      url: url_,
      headers: {
        "Accept": "application/json"
      },
      cancelToken
    };

    return this.instance.request(options_).catch((_error: any) => {
      if (isAxiosError(_error) && _error.response) {
        return _error.response;
      } else {
        throw _error;
      }
    }).then((_response: AxiosResponse) => {
      return this.processListCustomers(_response);
    });
  }

  protected processListCustomers(response: AxiosResponse): Promise<CustomerInfo[]> {
    const status = response.status;
    let _headers: any = {};
    if (response.headers && typeof response.headers === "object") {
      for (const k in response.headers) {
        if (response.headers.hasOwnProperty(k)) {
          _headers[k] = response.headers[k];
        }
      }
    }
    if (status === 200) {
      const _responseText = response.data;
      let result200: any = null;
      let resultData200 = _responseText;
      if (Array.isArray(resultData200)) {
        result200 = [] as any;
        for (let item of resultData200)
          result200!.push(CustomerInfo.fromJS(item));
      } else {
        result200 = null as any;
      }
      return Promise.resolve<CustomerInfo[]>(result200);

    } else if (status !== 200 && status !== 204) {
      const _responseText = response.data;
      return throwException("An unexpected server error occurred.", status, _responseText, _headers);
    }
    return Promise.resolve<CustomerInfo[]>(null as any);
  }

  /**
   * Deletes a customer.
   * @param id The email domain (ID) of the customer.
   * @return A Task representing the asynchronous operation.
   */
  deleteCustomer(id: string, cancelToken?: CancelToken): Promise<void> {
    let url_ = this.baseUrl + "/api/v1/customer/{id}";
    if (id === undefined || id === null)
      throw new globalThis.Error("The parameter 'id' must be defined.");
    url_ = url_.replace("{id}", encodeURIComponent("" + id));
    url_ = url_.replace(/[?&]$/, "");

    let options_: AxiosRequestConfig = {
      method: "DELETE",
      url: url_,
      headers: {},
      cancelToken
    };

    return this.instance.request(options_).catch((_error: any) => {
      if (isAxiosError(_error) && _error.response) {
        return _error.response;
      } else {
        throw _error;
      }
    }).then((_response: AxiosResponse) => {
      return this.processDeleteCustomer(_response);
    });
  }

  protected processDeleteCustomer(response: AxiosResponse): Promise<void> {
    const status = response.status;
    let _headers: any = {};
    if (response.headers && typeof response.headers === "object") {
      for (const k in response.headers) {
        if (response.headers.hasOwnProperty(k)) {
          _headers[k] = response.headers[k];
        }
      }
    }
    if (status === 204) {
      const _responseText = response.data;
      return Promise.resolve<void>(null as any);

    } else if (status === 400) {
      const _responseText = response.data;
      let result400: any = null;
      let resultData400 = _responseText;
      result400 = ProblemDetails.fromJS(resultData400);
      return throwException("A server side error occurred.", status, _responseText, _headers, result400);

    } else if (status === 404) {
      const _responseText = response.data;
      let result404: any = null;
      let resultData404 = _responseText;
      result404 = ProblemDetails.fromJS(resultData404);
      return throwException("A server side error occurred.", status, _responseText, _headers, result404);

    } else if (status !== 200 && status !== 204) {
      const _responseText = response.data;
      return throwException("An unexpected server error occurred.", status, _responseText, _headers);
    }
    return Promise.resolve<void>(null as any);
  }

  /**
   * Gets a customer by ID.
   * @param id The customer ID (email domain).
   * @return The customer, or null if none matches the ID.
   */
  getCustomer(id: string, cancelToken?: CancelToken): Promise<CustomerInfo> {
    let url_ = this.baseUrl + "/api/v1/customer/{id}";
    if (id === undefined || id === null)
      throw new globalThis.Error("The parameter 'id' must be defined.");
    url_ = url_.replace("{id}", encodeURIComponent("" + id));
    url_ = url_.replace(/[?&]$/, "");

    let options_: AxiosRequestConfig = {
      method: "GET",
      url: url_,
      headers: {
        "Accept": "application/json"
      },
      cancelToken
    };

    return this.instance.request(options_).catch((_error: any) => {
      if (isAxiosError(_error) && _error.response) {
        return _error.response;
      } else {
        throw _error;
      }
    }).then((_response: AxiosResponse) => {
      return this.processGetCustomer(_response);
    });
  }

  protected processGetCustomer(response: AxiosResponse): Promise<CustomerInfo> {
    const status = response.status;
    let _headers: any = {};
    if (response.headers && typeof response.headers === "object") {
      for (const k in response.headers) {
        if (response.headers.hasOwnProperty(k)) {
          _headers[k] = response.headers[k];
        }
      }
    }
    if (status === 200) {
      const _responseText = response.data;
      let result200: any = null;
      let resultData200 = _responseText;
      result200 = CustomerInfo.fromJS(resultData200);
      return Promise.resolve<CustomerInfo>(result200);

    } else if (status === 404) {
      const _responseText = response.data;
      let result404: any = null;
      let resultData404 = _responseText;
      result404 = ProblemDetails.fromJS(resultData404);
      return throwException("A server side error occurred.", status, _responseText, _headers, result404);

    } else if (status !== 200 && status !== 204) {
      const _responseText = response.data;
      return throwException("An unexpected server error occurred.", status, _responseText, _headers);
    }
    return Promise.resolve<CustomerInfo>(null as any);
  }

  /**
   * Edits a customer.
   * @param id The email domain (ID) of the customer.
   * @param dto (optional) A DTO containing the information to edit.
   * @return A Task representing the asynchronous operation.
   */
  updateCustomer(id: string, dto?: EditCustomerRequest | null | undefined, cancelToken?: CancelToken): Promise<void> {
    let url_ = this.baseUrl + "/api/v1/customer/{id}";
    if (id === undefined || id === null)
      throw new globalThis.Error("The parameter 'id' must be defined.");
    url_ = url_.replace("{id}", encodeURIComponent("" + id));
    url_ = url_.replace(/[?&]$/, "");

    const content_ = JSON.stringify(dto);

    let options_: AxiosRequestConfig = {
      data: content_,
      method: "PUT",
      url: url_,
      headers: {
        "Content-Type": "application/json",
      },
      cancelToken
    };

    return this.instance.request(options_).catch((_error: any) => {
      if (isAxiosError(_error) && _error.response) {
        return _error.response;
      } else {
        throw _error;
      }
    }).then((_response: AxiosResponse) => {
      return this.processUpdateCustomer(_response);
    });
  }

  protected processUpdateCustomer(response: AxiosResponse): Promise<void> {
    const status = response.status;
    let _headers: any = {};
    if (response.headers && typeof response.headers === "object") {
      for (const k in response.headers) {
        if (response.headers.hasOwnProperty(k)) {
          _headers[k] = response.headers[k];
        }
      }
    }
    if (status === 204) {
      const _responseText = response.data;
      return Promise.resolve<void>(null as any);

    } else if (status === 400) {
      const _responseText = response.data;
      let result400: any = null;
      let resultData400 = _responseText;
      result400 = ProblemDetails.fromJS(resultData400);
      return throwException("A server side error occurred.", status, _responseText, _headers, result400);

    } else if (status === 404) {
      const _responseText = response.data;
      let result404: any = null;
      let resultData404 = _responseText;
      result404 = ProblemDetails.fromJS(resultData404);
      return throwException("A server side error occurred.", status, _responseText, _headers, result404);

    } else if (status !== 200 && status !== 204) {
      const _responseText = response.data;
      return throwException("An unexpected server error occurred.", status, _responseText, _headers);
    }
    return Promise.resolve<void>(null as any);
  }

  /**
   * Returns the application health status.
   * @return The application health status.
   */
  heartbeat(cancelToken?: CancelToken): Promise<HealthStatus> {
    let url_ = this.baseUrl + "/api/v1/health";
    url_ = url_.replace(/[?&]$/, "");

    let options_: AxiosRequestConfig = {
      method: "GET",
      url: url_,
      headers: {
        "Accept": "application/json"
      },
      cancelToken
    };

    return this.instance.request(options_).catch((_error: any) => {
      if (isAxiosError(_error) && _error.response) {
        return _error.response;
      } else {
        throw _error;
      }
    }).then((_response: AxiosResponse) => {
      return this.processHeartbeat(_response);
    });
  }

  protected processHeartbeat(response: AxiosResponse): Promise<HealthStatus> {
    const status = response.status;
    let _headers: any = {};
    if (response.headers && typeof response.headers === "object") {
      for (const k in response.headers) {
        if (response.headers.hasOwnProperty(k)) {
          _headers[k] = response.headers[k];
        }
      }
    }
    if (status === 200) {
      const _responseText = response.data;
      let result200: any = null;
      let resultData200 = _responseText;
      result200 = HealthStatus.fromJS(resultData200);
      return Promise.resolve<HealthStatus>(result200);

    } else if (status !== 200 && status !== 204) {
      const _responseText = response.data;
      return throwException("An unexpected server error occurred.", status, _responseText, _headers);
    }
    return Promise.resolve<HealthStatus>(null as any);
  }

  getProject(hubId: string | null, projectId: string, storageType?: StorageType | null | undefined, cancelToken?: CancelToken): Promise<Project> {
    let url_ = this.baseUrl + "/api/v1/project/{hubId}/{projectId}?";
    if (hubId === undefined || hubId === null)
      throw new globalThis.Error("The parameter 'hubId' must be defined.");
    url_ = url_.replace("{hubId}", encodeURIComponent("" + hubId));
    if (projectId === undefined || projectId === null)
      throw new globalThis.Error("The parameter 'projectId' must be defined.");
    url_ = url_.replace("{projectId}", encodeURIComponent("" + projectId));
    if (storageType !== undefined && storageType !== null)
      url_ += "storageType=" + encodeURIComponent("" + storageType) + "&";
    url_ = url_.replace(/[?&]$/, "");

    let options_: AxiosRequestConfig = {
      method: "GET",
      url: url_,
      headers: {
        "Accept": "application/json"
      },
      cancelToken
    };

    return this.instance.request(options_).catch((_error: any) => {
      if (isAxiosError(_error) && _error.response) {
        return _error.response;
      } else {
        throw _error;
      }
    }).then((_response: AxiosResponse) => {
      return this.processGetProject(_response);
    });
  }

  protected processGetProject(response: AxiosResponse): Promise<Project> {
    const status = response.status;
    let _headers: any = {};
    if (response.headers && typeof response.headers === "object") {
      for (const k in response.headers) {
        if (response.headers.hasOwnProperty(k)) {
          _headers[k] = response.headers[k];
        }
      }
    }
    if (status === 200) {
      const _responseText = response.data;
      let result200: any = null;
      let resultData200 = _responseText;
      result200 = Project.fromJS(resultData200);
      return Promise.resolve<Project>(result200);

    } else if (status === 404) {
      const _responseText = response.data;
      let result404: any = null;
      let resultData404 = _responseText;
      result404 = ProblemDetails.fromJS(resultData404);
      return throwException("A server side error occurred.", status, _responseText, _headers, result404);

    } else if (status !== 200 && status !== 204) {
      const _responseText = response.data;
      return throwException("An unexpected server error occurred.", status, _responseText, _headers);
    }
    return Promise.resolve<Project>(null as any);
  }

  getProjectRvtVersionDetection(id: string, cancelToken?: CancelToken): Promise<ProjectRevitVersionDetectionResult> {
    let url_ = this.baseUrl + "/api/v1/project/version/{id}";
    if (id === undefined || id === null)
      throw new globalThis.Error("The parameter 'id' must be defined.");
    url_ = url_.replace("{id}", encodeURIComponent("" + id));
    url_ = url_.replace(/[?&]$/, "");

    let options_: AxiosRequestConfig = {
      method: "GET",
      url: url_,
      headers: {
        "Accept": "application/json"
      },
      cancelToken
    };

    return this.instance.request(options_).catch((_error: any) => {
      if (isAxiosError(_error) && _error.response) {
        return _error.response;
      } else {
        throw _error;
      }
    }).then((_response: AxiosResponse) => {
      return this.processGetProjectRvtVersionDetection(_response);
    });
  }

  protected processGetProjectRvtVersionDetection(response: AxiosResponse): Promise<ProjectRevitVersionDetectionResult> {
    const status = response.status;
    let _headers: any = {};
    if (response.headers && typeof response.headers === "object") {
      for (const k in response.headers) {
        if (response.headers.hasOwnProperty(k)) {
          _headers[k] = response.headers[k];
        }
      }
    }
    if (status === 200) {
      const _responseText = response.data;
      let result200: any = null;
      let resultData200 = _responseText;
      result200 = ProjectRevitVersionDetectionResult.fromJS(resultData200);
      return Promise.resolve<ProjectRevitVersionDetectionResult>(result200);

    } else if (status === 404) {
      const _responseText = response.data;
      let result404: any = null;
      let resultData404 = _responseText;
      result404 = ProblemDetails.fromJS(resultData404);
      return throwException("A server side error occurred.", status, _responseText, _headers, result404);

    } else if (status !== 200 && status !== 204) {
      const _responseText = response.data;
      return throwException("An unexpected server error occurred.", status, _responseText, _headers);
    }
    return Promise.resolve<ProjectRevitVersionDetectionResult>(null as any);
  }

  getProjectRvtVersionNon(hubId: string | null, projectId: string, checkDeleted?: boolean | undefined, cancelToken?: CancelToken): Promise<GetRvtVersionResponse> {
    let url_ = this.baseUrl + "/api/v1/project/{hubId}/{projectId}/version?";
    if (hubId === undefined || hubId === null)
      throw new globalThis.Error("The parameter 'hubId' must be defined.");
    url_ = url_.replace("{hubId}", encodeURIComponent("" + hubId));
    if (projectId === undefined || projectId === null)
      throw new globalThis.Error("The parameter 'projectId' must be defined.");
    url_ = url_.replace("{projectId}", encodeURIComponent("" + projectId));
    if (checkDeleted === null)
      throw new globalThis.Error("The parameter 'checkDeleted' cannot be null.");
    else if (checkDeleted !== undefined)
      url_ += "checkDeleted=" + encodeURIComponent("" + checkDeleted) + "&";
    url_ = url_.replace(/[?&]$/, "");

    let options_: AxiosRequestConfig = {
      method: "GET",
      url: url_,
      headers: {
        "Accept": "application/json"
      },
      cancelToken
    };

    return this.instance.request(options_).catch((_error: any) => {
      if (isAxiosError(_error) && _error.response) {
        return _error.response;
      } else {
        throw _error;
      }
    }).then((_response: AxiosResponse) => {
      return this.processGetProjectRvtVersionNon(_response);
    });
  }

  protected processGetProjectRvtVersionNon(response: AxiosResponse): Promise<GetRvtVersionResponse> {
    const status = response.status;
    let _headers: any = {};
    if (response.headers && typeof response.headers === "object") {
      for (const k in response.headers) {
        if (response.headers.hasOwnProperty(k)) {
          _headers[k] = response.headers[k];
        }
      }
    }
    if (status === 200) {
      const _responseText = response.data;
      let result200: any = null;
      let resultData200 = _responseText;
      result200 = GetRvtVersionResponse.fromJS(resultData200);
      return Promise.resolve<GetRvtVersionResponse>(result200);

    } else if (status === 404) {
      const _responseText = response.data;
      let result404: any = null;
      let resultData404 = _responseText;
      result404 = ProblemDetails.fromJS(resultData404);
      return throwException("A server side error occurred.", status, _responseText, _headers, result404);

    } else if (status !== 200 && status !== 204) {
      const _responseText = response.data;
      return throwException("An unexpected server error occurred.", status, _responseText, _headers);
    }
    return Promise.resolve<GetRvtVersionResponse>(null as any);
  }

  listAll(includeLegacyHubs?: boolean | undefined, cancelToken?: CancelToken): Promise<Project[]> {
    let url_ = this.baseUrl + "/api/v1/project?";
    if (includeLegacyHubs === null)
      throw new globalThis.Error("The parameter 'includeLegacyHubs' cannot be null.");
    else if (includeLegacyHubs !== undefined)
      url_ += "includeLegacyHubs=" + encodeURIComponent("" + includeLegacyHubs) + "&";
    url_ = url_.replace(/[?&]$/, "");

    let options_: AxiosRequestConfig = {
      method: "GET",
      url: url_,
      headers: {
        "Accept": "application/json"
      },
      cancelToken
    };

    return this.instance.request(options_).catch((_error: any) => {
      if (isAxiosError(_error) && _error.response) {
        return _error.response;
      } else {
        throw _error;
      }
    }).then((_response: AxiosResponse) => {
      return this.processListAll(_response);
    });
  }

  protected processListAll(response: AxiosResponse): Promise<Project[]> {
    const status = response.status;
    let _headers: any = {};
    if (response.headers && typeof response.headers === "object") {
      for (const k in response.headers) {
        if (response.headers.hasOwnProperty(k)) {
          _headers[k] = response.headers[k];
        }
      }
    }
    if (status === 200) {
      const _responseText = response.data;
      let result200: any = null;
      let resultData200 = _responseText;
      if (Array.isArray(resultData200)) {
        result200 = [] as any;
        for (let item of resultData200)
          result200!.push(Project.fromJS(item));
      } else {
        result200 = null as any;
      }
      return Promise.resolve<Project[]>(result200);

    } else if (status !== 200 && status !== 204) {
      const _responseText = response.data;
      return throwException("An unexpected server error occurred.", status, _responseText, _headers);
    }
    return Promise.resolve<Project[]>(null as any);
  }

  listHubs(includeLegacyHubs?: boolean | undefined, cancelToken?: CancelToken): Promise<Hub[]> {
    let url_ = this.baseUrl + "/api/v1/project/hubs?";
    if (includeLegacyHubs === null)
      throw new globalThis.Error("The parameter 'includeLegacyHubs' cannot be null.");
    else if (includeLegacyHubs !== undefined)
      url_ += "includeLegacyHubs=" + encodeURIComponent("" + includeLegacyHubs) + "&";
    url_ = url_.replace(/[?&]$/, "");

    let options_: AxiosRequestConfig = {
      method: "GET",
      url: url_,
      headers: {
        "Accept": "application/json"
      },
      cancelToken
    };

    return this.instance.request(options_).catch((_error: any) => {
      if (isAxiosError(_error) && _error.response) {
        return _error.response;
      } else {
        throw _error;
      }
    }).then((_response: AxiosResponse) => {
      return this.processListHubs(_response);
    });
  }

  protected processListHubs(response: AxiosResponse): Promise<Hub[]> {
    const status = response.status;
    let _headers: any = {};
    if (response.headers && typeof response.headers === "object") {
      for (const k in response.headers) {
        if (response.headers.hasOwnProperty(k)) {
          _headers[k] = response.headers[k];
        }
      }
    }
    if (status === 200) {
      const _responseText = response.data;
      let result200: any = null;
      let resultData200 = _responseText;
      if (Array.isArray(resultData200)) {
        result200 = [] as any;
        for (let item of resultData200)
          result200!.push(Hub.fromJS(item));
      } else {
        result200 = null as any;
      }
      return Promise.resolve<Hub[]>(result200);

    } else if (status !== 200 && status !== 204) {
      const _responseText = response.data;
      return throwException("An unexpected server error occurred.", status, _responseText, _headers);
    }
    return Promise.resolve<Hub[]>(null as any);
  }

  listImages(includeLegacyHubs?: boolean | undefined, cancelToken?: CancelToken): Promise<Project[]> {
    let url_ = this.baseUrl + "/api/v1/project/images?";
    if (includeLegacyHubs === null)
      throw new globalThis.Error("The parameter 'includeLegacyHubs' cannot be null.");
    else if (includeLegacyHubs !== undefined)
      url_ += "includeLegacyHubs=" + encodeURIComponent("" + includeLegacyHubs) + "&";
    url_ = url_.replace(/[?&]$/, "");

    let options_: AxiosRequestConfig = {
      method: "GET",
      url: url_,
      headers: {
        "Accept": "application/json"
      },
      cancelToken
    };

    return this.instance.request(options_).catch((_error: any) => {
      if (isAxiosError(_error) && _error.response) {
        return _error.response;
      } else {
        throw _error;
      }
    }).then((_response: AxiosResponse) => {
      return this.processListImages(_response);
    });
  }

  protected processListImages(response: AxiosResponse): Promise<Project[]> {
    const status = response.status;
    let _headers: any = {};
    if (response.headers && typeof response.headers === "object") {
      for (const k in response.headers) {
        if (response.headers.hasOwnProperty(k)) {
          _headers[k] = response.headers[k];
        }
      }
    }
    if (status === 200) {
      const _responseText = response.data;
      let result200: any = null;
      let resultData200 = _responseText;
      if (Array.isArray(resultData200)) {
        result200 = [] as any;
        for (let item of resultData200)
          result200!.push(Project.fromJS(item));
      } else {
        result200 = null as any;
      }
      return Promise.resolve<Project[]>(result200);

    } else if (status !== 200 && status !== 204) {
      const _responseText = response.data;
      return throwException("An unexpected server error occurred.", status, _responseText, _headers);
    }
    return Promise.resolve<Project[]>(null as any);
  }

  startProjectRvtVersionDetection(request?: StartProjectRevitVersionDetectionRequest | null | undefined, cancelToken?: CancelToken): Promise<ProjectRevitVersionDetectionResult> {
    let url_ = this.baseUrl + "/api/v1/project/version";
    url_ = url_.replace(/[?&]$/, "");

    const content_ = JSON.stringify(request);

    let options_: AxiosRequestConfig = {
      data: content_,
      method: "POST",
      url: url_,
      headers: {
        "Content-Type": "application/json",
        "Accept": "application/json"
      },
      cancelToken
    };

    return this.instance.request(options_).catch((_error: any) => {
      if (isAxiosError(_error) && _error.response) {
        return _error.response;
      } else {
        throw _error;
      }
    }).then((_response: AxiosResponse) => {
      return this.processStartProjectRvtVersionDetection(_response);
    });
  }

  protected processStartProjectRvtVersionDetection(response: AxiosResponse): Promise<ProjectRevitVersionDetectionResult> {
    const status = response.status;
    let _headers: any = {};
    if (response.headers && typeof response.headers === "object") {
      for (const k in response.headers) {
        if (response.headers.hasOwnProperty(k)) {
          _headers[k] = response.headers[k];
        }
      }
    }
    if (status === 200) {
      const _responseText = response.data;
      let result200: any = null;
      let resultData200 = _responseText;
      result200 = ProjectRevitVersionDetectionResult.fromJS(resultData200);
      return Promise.resolve<ProjectRevitVersionDetectionResult>(result200);

    } else if (status === 202) {
      const _responseText = response.data;
      let result202: any = null;
      let resultData202 = _responseText;
      result202 = ProjectRevitVersionDetectionResult.fromJS(resultData202);
      return Promise.resolve<ProjectRevitVersionDetectionResult>(result202);

    } else if (status === 400) {
      const _responseText = response.data;
      let result400: any = null;
      let resultData400 = _responseText;
      result400 = ProblemDetails.fromJS(resultData400);
      return throwException("A server side error occurred.", status, _responseText, _headers, result400);

    } else if (status === 404) {
      const _responseText = response.data;
      let result404: any = null;
      let resultData404 = _responseText;
      result404 = ProblemDetails.fromJS(resultData404);
      return throwException("A server side error occurred.", status, _responseText, _headers, result404);

    } else if (status !== 200 && status !== 204) {
      const _responseText = response.data;
      return throwException("An unexpected server error occurred.", status, _responseText, _headers);
    }
    return Promise.resolve<ProjectRevitVersionDetectionResult>(null as any);
  }

  createProjectWiseConfiguration(dto?: CreateProjectWiseConfigurationDto | null | undefined, cancelToken?: CancelToken): Promise<ProjectWiseConfiguration> {
    let url_ = this.baseUrl + "/api/v1/settings/pw/configurations";
    url_ = url_.replace(/[?&]$/, "");

    const content_ = JSON.stringify(dto);

    let options_: AxiosRequestConfig = {
      data: content_,
      method: "POST",
      url: url_,
      headers: {
        "Content-Type": "application/json",
        "Accept": "application/json"
      },
      cancelToken
    };

    return this.instance.request(options_).catch((_error: any) => {
      if (isAxiosError(_error) && _error.response) {
        return _error.response;
      } else {
        throw _error;
      }
    }).then((_response: AxiosResponse) => {
      return this.processCreateProjectWiseConfiguration(_response);
    });
  }

  protected processCreateProjectWiseConfiguration(response: AxiosResponse): Promise<ProjectWiseConfiguration> {
    const status = response.status;
    let _headers: any = {};
    if (response.headers && typeof response.headers === "object") {
      for (const k in response.headers) {
        if (response.headers.hasOwnProperty(k)) {
          _headers[k] = response.headers[k];
        }
      }
    }
    if (status === 201) {
      const _responseText = response.data;
      let result201: any = null;
      let resultData201 = _responseText;
      result201 = ProjectWiseConfiguration.fromJS(resultData201);
      return Promise.resolve<ProjectWiseConfiguration>(result201);

    } else if (status === 400) {
      const _responseText = response.data;
      let result400: any = null;
      let resultData400 = _responseText;
      result400 = ProblemDetails.fromJS(resultData400);
      return throwException("A server side error occurred.", status, _responseText, _headers, result400);

    } else if (status !== 200 && status !== 204) {
      const _responseText = response.data;
      return throwException("An unexpected server error occurred.", status, _responseText, _headers);
    }
    return Promise.resolve<ProjectWiseConfiguration>(null as any);
  }

  createProjectWiseCredential(dto?: CreateProjectWiseCredentialDto | null | undefined, cancelToken?: CancelToken): Promise<ProjectWiseCredential> {
    let url_ = this.baseUrl + "/api/v1/settings/pw/credentials";
    url_ = url_.replace(/[?&]$/, "");

    const content_ = JSON.stringify(dto);

    let options_: AxiosRequestConfig = {
      data: content_,
      method: "POST",
      url: url_,
      headers: {
        "Content-Type": "application/json",
        "Accept": "application/json"
      },
      cancelToken
    };

    return this.instance.request(options_).catch((_error: any) => {
      if (isAxiosError(_error) && _error.response) {
        return _error.response;
      } else {
        throw _error;
      }
    }).then((_response: AxiosResponse) => {
      return this.processCreateProjectWiseCredential(_response);
    });
  }

  protected processCreateProjectWiseCredential(response: AxiosResponse): Promise<ProjectWiseCredential> {
    const status = response.status;
    let _headers: any = {};
    if (response.headers && typeof response.headers === "object") {
      for (const k in response.headers) {
        if (response.headers.hasOwnProperty(k)) {
          _headers[k] = response.headers[k];
        }
      }
    }
    if (status === 201) {
      const _responseText = response.data;
      let result201: any = null;
      let resultData201 = _responseText;
      result201 = ProjectWiseCredential.fromJS(resultData201);
      return Promise.resolve<ProjectWiseCredential>(result201);

    } else if (status === 400) {
      const _responseText = response.data;
      let result400: any = null;
      let resultData400 = _responseText;
      result400 = ProblemDetails.fromJS(resultData400);
      return throwException("A server side error occurred.", status, _responseText, _headers, result400);

    } else if (status !== 200 && status !== 204) {
      const _responseText = response.data;
      return throwException("An unexpected server error occurred.", status, _responseText, _headers);
    }
    return Promise.resolve<ProjectWiseCredential>(null as any);
  }

  listProjectWiseCredentials(paginationToken?: string | null | undefined, limit?: number | undefined, cancelToken?: CancelToken): Promise<ProjectWiseCredentialPaginatedResult> {
    let url_ = this.baseUrl + "/api/v1/settings/pw/credentials?";
    if (paginationToken !== undefined && paginationToken !== null)
      url_ += "PaginationToken=" + encodeURIComponent("" + paginationToken) + "&";
    if (limit === null)
      throw new globalThis.Error("The parameter 'limit' cannot be null.");
    else if (limit !== undefined)
      url_ += "Limit=" + encodeURIComponent("" + limit) + "&";
    url_ = url_.replace(/[?&]$/, "");

    let options_: AxiosRequestConfig = {
      method: "GET",
      url: url_,
      headers: {
        "Accept": "application/json"
      },
      cancelToken
    };

    return this.instance.request(options_).catch((_error: any) => {
      if (isAxiosError(_error) && _error.response) {
        return _error.response;
      } else {
        throw _error;
      }
    }).then((_response: AxiosResponse) => {
      return this.processListProjectWiseCredentials(_response);
    });
  }

  protected processListProjectWiseCredentials(response: AxiosResponse): Promise<ProjectWiseCredentialPaginatedResult> {
    const status = response.status;
    let _headers: any = {};
    if (response.headers && typeof response.headers === "object") {
      for (const k in response.headers) {
        if (response.headers.hasOwnProperty(k)) {
          _headers[k] = response.headers[k];
        }
      }
    }
    if (status === 200) {
      const _responseText = response.data;
      let result200: any = null;
      let resultData200 = _responseText;
      result200 = ProjectWiseCredentialPaginatedResult.fromJS(resultData200);
      return Promise.resolve<ProjectWiseCredentialPaginatedResult>(result200);

    } else if (status === 400) {
      const _responseText = response.data;
      let result400: any = null;
      let resultData400 = _responseText;
      result400 = ProblemDetails.fromJS(resultData400);
      return throwException("A server side error occurred.", status, _responseText, _headers, result400);

    } else if (status !== 200 && status !== 204) {
      const _responseText = response.data;
      return throwException("An unexpected server error occurred.", status, _responseText, _headers);
    }
    return Promise.resolve<ProjectWiseCredentialPaginatedResult>(null as any);
  }

  deleteProjectWiseConfiguration(id: string, cancelToken?: CancelToken): Promise<void> {
    let url_ = this.baseUrl + "/api/v1/settings/pw/configurations/{id}";
    if (id === undefined || id === null)
      throw new globalThis.Error("The parameter 'id' must be defined.");
    url_ = url_.replace("{id}", encodeURIComponent("" + id));
    url_ = url_.replace(/[?&]$/, "");

    let options_: AxiosRequestConfig = {
      method: "DELETE",
      url: url_,
      headers: {},
      cancelToken
    };

    return this.instance.request(options_).catch((_error: any) => {
      if (isAxiosError(_error) && _error.response) {
        return _error.response;
      } else {
        throw _error;
      }
    }).then((_response: AxiosResponse) => {
      return this.processDeleteProjectWiseConfiguration(_response);
    });
  }

  protected processDeleteProjectWiseConfiguration(response: AxiosResponse): Promise<void> {
    const status = response.status;
    let _headers: any = {};
    if (response.headers && typeof response.headers === "object") {
      for (const k in response.headers) {
        if (response.headers.hasOwnProperty(k)) {
          _headers[k] = response.headers[k];
        }
      }
    }
    if (status === 204) {
      const _responseText = response.data;
      return Promise.resolve<void>(null as any);

    } else if (status === 403) {
      const _responseText = response.data;
      let result403: any = null;
      let resultData403 = _responseText;
      result403 = ProblemDetails.fromJS(resultData403);
      return throwException("A server side error occurred.", status, _responseText, _headers, result403);

    } else if (status === 404) {
      const _responseText = response.data;
      let result404: any = null;
      let resultData404 = _responseText;
      result404 = ProblemDetails.fromJS(resultData404);
      return throwException("A server side error occurred.", status, _responseText, _headers, result404);

    } else if (status !== 200 && status !== 204) {
      const _responseText = response.data;
      return throwException("An unexpected server error occurred.", status, _responseText, _headers);
    }
    return Promise.resolve<void>(null as any);
  }

  getProjectWiseConfiguration(id: string, allowDeleted?: boolean | undefined, cancelToken?: CancelToken): Promise<ProjectWiseConfiguration> {
    let url_ = this.baseUrl + "/api/v1/settings/pw/configurations/{id}?";
    if (id === undefined || id === null)
      throw new globalThis.Error("The parameter 'id' must be defined.");
    url_ = url_.replace("{id}", encodeURIComponent("" + id));
    if (allowDeleted === null)
      throw new globalThis.Error("The parameter 'allowDeleted' cannot be null.");
    else if (allowDeleted !== undefined)
      url_ += "allowDeleted=" + encodeURIComponent("" + allowDeleted) + "&";
    url_ = url_.replace(/[?&]$/, "");

    let options_: AxiosRequestConfig = {
      method: "GET",
      url: url_,
      headers: {
        "Accept": "application/json"
      },
      cancelToken
    };

    return this.instance.request(options_).catch((_error: any) => {
      if (isAxiosError(_error) && _error.response) {
        return _error.response;
      } else {
        throw _error;
      }
    }).then((_response: AxiosResponse) => {
      return this.processGetProjectWiseConfiguration(_response);
    });
  }

  protected processGetProjectWiseConfiguration(response: AxiosResponse): Promise<ProjectWiseConfiguration> {
    const status = response.status;
    let _headers: any = {};
    if (response.headers && typeof response.headers === "object") {
      for (const k in response.headers) {
        if (response.headers.hasOwnProperty(k)) {
          _headers[k] = response.headers[k];
        }
      }
    }
    if (status === 200) {
      const _responseText = response.data;
      let result200: any = null;
      let resultData200 = _responseText;
      result200 = ProjectWiseConfiguration.fromJS(resultData200);
      return Promise.resolve<ProjectWiseConfiguration>(result200);

    } else if (status === 403) {
      const _responseText = response.data;
      let result403: any = null;
      let resultData403 = _responseText;
      result403 = ProblemDetails.fromJS(resultData403);
      return throwException("A server side error occurred.", status, _responseText, _headers, result403);

    } else if (status === 404) {
      const _responseText = response.data;
      let result404: any = null;
      let resultData404 = _responseText;
      result404 = ProblemDetails.fromJS(resultData404);
      return throwException("A server side error occurred.", status, _responseText, _headers, result404);

    } else if (status !== 200 && status !== 204) {
      const _responseText = response.data;
      return throwException("An unexpected server error occurred.", status, _responseText, _headers);
    }
    return Promise.resolve<ProjectWiseConfiguration>(null as any);
  }

  updateProjectWiseConfiguration(id: string, dto?: UpdateProjectWiseConfigurationDto | null | undefined, cancelToken?: CancelToken): Promise<ProjectWiseConfiguration> {
    let url_ = this.baseUrl + "/api/v1/settings/pw/configurations/{id}";
    if (id === undefined || id === null)
      throw new globalThis.Error("The parameter 'id' must be defined.");
    url_ = url_.replace("{id}", encodeURIComponent("" + id));
    url_ = url_.replace(/[?&]$/, "");

    const content_ = JSON.stringify(dto);

    let options_: AxiosRequestConfig = {
      data: content_,
      method: "POST",
      url: url_,
      headers: {
        "Content-Type": "application/json",
        "Accept": "application/json"
      },
      cancelToken
    };

    return this.instance.request(options_).catch((_error: any) => {
      if (isAxiosError(_error) && _error.response) {
        return _error.response;
      } else {
        throw _error;
      }
    }).then((_response: AxiosResponse) => {
      return this.processUpdateProjectWiseConfiguration(_response);
    });
  }

  protected processUpdateProjectWiseConfiguration(response: AxiosResponse): Promise<ProjectWiseConfiguration> {
    const status = response.status;
    let _headers: any = {};
    if (response.headers && typeof response.headers === "object") {
      for (const k in response.headers) {
        if (response.headers.hasOwnProperty(k)) {
          _headers[k] = response.headers[k];
        }
      }
    }
    if (status === 200) {
      const _responseText = response.data;
      let result200: any = null;
      let resultData200 = _responseText;
      result200 = ProjectWiseConfiguration.fromJS(resultData200);
      return Promise.resolve<ProjectWiseConfiguration>(result200);

    } else if (status === 400) {
      const _responseText = response.data;
      let result400: any = null;
      let resultData400 = _responseText;
      result400 = ProblemDetails.fromJS(resultData400);
      return throwException("A server side error occurred.", status, _responseText, _headers, result400);

    } else if (status !== 200 && status !== 204) {
      const _responseText = response.data;
      return throwException("An unexpected server error occurred.", status, _responseText, _headers);
    }
    return Promise.resolve<ProjectWiseConfiguration>(null as any);
  }

  deleteProjectWiseCredential(id: string, cancelToken?: CancelToken): Promise<void> {
    let url_ = this.baseUrl + "/api/v1/settings/pw/credentials/{id}";
    if (id === undefined || id === null)
      throw new globalThis.Error("The parameter 'id' must be defined.");
    url_ = url_.replace("{id}", encodeURIComponent("" + id));
    url_ = url_.replace(/[?&]$/, "");

    let options_: AxiosRequestConfig = {
      method: "DELETE",
      url: url_,
      headers: {},
      cancelToken
    };

    return this.instance.request(options_).catch((_error: any) => {
      if (isAxiosError(_error) && _error.response) {
        return _error.response;
      } else {
        throw _error;
      }
    }).then((_response: AxiosResponse) => {
      return this.processDeleteProjectWiseCredential(_response);
    });
  }

  protected processDeleteProjectWiseCredential(response: AxiosResponse): Promise<void> {
    const status = response.status;
    let _headers: any = {};
    if (response.headers && typeof response.headers === "object") {
      for (const k in response.headers) {
        if (response.headers.hasOwnProperty(k)) {
          _headers[k] = response.headers[k];
        }
      }
    }
    if (status === 204) {
      const _responseText = response.data;
      return Promise.resolve<void>(null as any);

    } else if (status === 403) {
      const _responseText = response.data;
      let result403: any = null;
      let resultData403 = _responseText;
      result403 = ProblemDetails.fromJS(resultData403);
      return throwException("A server side error occurred.", status, _responseText, _headers, result403);

    } else if (status === 404) {
      const _responseText = response.data;
      let result404: any = null;
      let resultData404 = _responseText;
      result404 = ProblemDetails.fromJS(resultData404);
      return throwException("A server side error occurred.", status, _responseText, _headers, result404);

    } else if (status !== 200 && status !== 204) {
      const _responseText = response.data;
      return throwException("An unexpected server error occurred.", status, _responseText, _headers);
    }
    return Promise.resolve<void>(null as any);
  }

  getProjectWiseCredential(id: string, allowDeleted?: boolean | undefined, cancelToken?: CancelToken): Promise<ProjectWiseCredential> {
    let url_ = this.baseUrl + "/api/v1/settings/pw/credentials/{id}?";
    if (id === undefined || id === null)
      throw new globalThis.Error("The parameter 'id' must be defined.");
    url_ = url_.replace("{id}", encodeURIComponent("" + id));
    if (allowDeleted === null)
      throw new globalThis.Error("The parameter 'allowDeleted' cannot be null.");
    else if (allowDeleted !== undefined)
      url_ += "allowDeleted=" + encodeURIComponent("" + allowDeleted) + "&";
    url_ = url_.replace(/[?&]$/, "");

    let options_: AxiosRequestConfig = {
      method: "GET",
      url: url_,
      headers: {
        "Accept": "application/json"
      },
      cancelToken
    };

    return this.instance.request(options_).catch((_error: any) => {
      if (isAxiosError(_error) && _error.response) {
        return _error.response;
      } else {
        throw _error;
      }
    }).then((_response: AxiosResponse) => {
      return this.processGetProjectWiseCredential(_response);
    });
  }

  protected processGetProjectWiseCredential(response: AxiosResponse): Promise<ProjectWiseCredential> {
    const status = response.status;
    let _headers: any = {};
    if (response.headers && typeof response.headers === "object") {
      for (const k in response.headers) {
        if (response.headers.hasOwnProperty(k)) {
          _headers[k] = response.headers[k];
        }
      }
    }
    if (status === 200) {
      const _responseText = response.data;
      let result200: any = null;
      let resultData200 = _responseText;
      result200 = ProjectWiseCredential.fromJS(resultData200);
      return Promise.resolve<ProjectWiseCredential>(result200);

    } else if (status === 403) {
      const _responseText = response.data;
      let result403: any = null;
      let resultData403 = _responseText;
      result403 = ProblemDetails.fromJS(resultData403);
      return throwException("A server side error occurred.", status, _responseText, _headers, result403);

    } else if (status === 404) {
      const _responseText = response.data;
      let result404: any = null;
      let resultData404 = _responseText;
      result404 = ProblemDetails.fromJS(resultData404);
      return throwException("A server side error occurred.", status, _responseText, _headers, result404);

    } else if (status !== 200 && status !== 204) {
      const _responseText = response.data;
      return throwException("An unexpected server error occurred.", status, _responseText, _headers);
    }
    return Promise.resolve<ProjectWiseCredential>(null as any);
  }

  updateProjectWiseCredential(id: string, dto?: UpdateProjectWiseCredentialDto | null | undefined, cancelToken?: CancelToken): Promise<ProjectWiseCredential> {
    let url_ = this.baseUrl + "/api/v1/settings/pw/credentials/{id}";
    if (id === undefined || id === null)
      throw new globalThis.Error("The parameter 'id' must be defined.");
    url_ = url_.replace("{id}", encodeURIComponent("" + id));
    url_ = url_.replace(/[?&]$/, "");

    const content_ = JSON.stringify(dto);

    let options_: AxiosRequestConfig = {
      data: content_,
      method: "PUT",
      url: url_,
      headers: {
        "Content-Type": "application/json",
        "Accept": "application/json"
      },
      cancelToken
    };

    return this.instance.request(options_).catch((_error: any) => {
      if (isAxiosError(_error) && _error.response) {
        return _error.response;
      } else {
        throw _error;
      }
    }).then((_response: AxiosResponse) => {
      return this.processUpdateProjectWiseCredential(_response);
    });
  }

  protected processUpdateProjectWiseCredential(response: AxiosResponse): Promise<ProjectWiseCredential> {
    const status = response.status;
    let _headers: any = {};
    if (response.headers && typeof response.headers === "object") {
      for (const k in response.headers) {
        if (response.headers.hasOwnProperty(k)) {
          _headers[k] = response.headers[k];
        }
      }
    }
    if (status === 200) {
      const _responseText = response.data;
      let result200: any = null;
      let resultData200 = _responseText;
      result200 = ProjectWiseCredential.fromJS(resultData200);
      return Promise.resolve<ProjectWiseCredential>(result200);

    } else if (status === 400) {
      const _responseText = response.data;
      let result400: any = null;
      let resultData400 = _responseText;
      result400 = ProblemDetails.fromJS(resultData400);
      return throwException("A server side error occurred.", status, _responseText, _headers, result400);

    } else if (status !== 200 && status !== 204) {
      const _responseText = response.data;
      return throwException("An unexpected server error occurred.", status, _responseText, _headers);
    }
    return Promise.resolve<ProjectWiseCredential>(null as any);
  }

  getProjectWiseCredentialSecret(id: string, cancelToken?: CancelToken): Promise<ProjectWiseCredentialWithSecret> {
    let url_ = this.baseUrl + "/api/v1/settings/pw/secret/{id}";
    if (id === undefined || id === null)
      throw new globalThis.Error("The parameter 'id' must be defined.");
    url_ = url_.replace("{id}", encodeURIComponent("" + id));
    url_ = url_.replace(/[?&]$/, "");

    let options_: AxiosRequestConfig = {
      method: "GET",
      url: url_,
      headers: {
        "Accept": "application/json"
      },
      cancelToken
    };

    return this.instance.request(options_).catch((_error: any) => {
      if (isAxiosError(_error) && _error.response) {
        return _error.response;
      } else {
        throw _error;
      }
    }).then((_response: AxiosResponse) => {
      return this.processGetProjectWiseCredentialSecret(_response);
    });
  }

  protected processGetProjectWiseCredentialSecret(response: AxiosResponse): Promise<ProjectWiseCredentialWithSecret> {
    const status = response.status;
    let _headers: any = {};
    if (response.headers && typeof response.headers === "object") {
      for (const k in response.headers) {
        if (response.headers.hasOwnProperty(k)) {
          _headers[k] = response.headers[k];
        }
      }
    }
    if (status === 200) {
      const _responseText = response.data;
      let result200: any = null;
      let resultData200 = _responseText;
      result200 = ProjectWiseCredentialWithSecret.fromJS(resultData200);
      return Promise.resolve<ProjectWiseCredentialWithSecret>(result200);

    } else if (status === 403) {
      const _responseText = response.data;
      let result403: any = null;
      let resultData403 = _responseText;
      result403 = ProblemDetails.fromJS(resultData403);
      return throwException("A server side error occurred.", status, _responseText, _headers, result403);

    } else if (status === 404) {
      const _responseText = response.data;
      let result404: any = null;
      let resultData404 = _responseText;
      result404 = ProblemDetails.fromJS(resultData404);
      return throwException("A server side error occurred.", status, _responseText, _headers, result404);

    } else if (status !== 200 && status !== 204) {
      const _responseText = response.data;
      return throwException("An unexpected server error occurred.", status, _responseText, _headers);
    }
    return Promise.resolve<ProjectWiseCredentialWithSecret>(null as any);
  }

  listProjectWiseConfigurations(paginationToken?: string | null | undefined, limit?: number | undefined, cancelToken?: CancelToken): Promise<ProjectWiseConfigurationPaginatedResult> {
    let url_ = this.baseUrl + "/api/v1/settings/pw/config?";
    if (paginationToken !== undefined && paginationToken !== null)
      url_ += "PaginationToken=" + encodeURIComponent("" + paginationToken) + "&";
    if (limit === null)
      throw new globalThis.Error("The parameter 'limit' cannot be null.");
    else if (limit !== undefined)
      url_ += "Limit=" + encodeURIComponent("" + limit) + "&";
    url_ = url_.replace(/[?&]$/, "");

    let options_: AxiosRequestConfig = {
      method: "GET",
      url: url_,
      headers: {
        "Accept": "application/json"
      },
      cancelToken
    };

    return this.instance.request(options_).catch((_error: any) => {
      if (isAxiosError(_error) && _error.response) {
        return _error.response;
      } else {
        throw _error;
      }
    }).then((_response: AxiosResponse) => {
      return this.processListProjectWiseConfigurations(_response);
    });
  }

  protected processListProjectWiseConfigurations(response: AxiosResponse): Promise<ProjectWiseConfigurationPaginatedResult> {
    const status = response.status;
    let _headers: any = {};
    if (response.headers && typeof response.headers === "object") {
      for (const k in response.headers) {
        if (response.headers.hasOwnProperty(k)) {
          _headers[k] = response.headers[k];
        }
      }
    }
    if (status === 200) {
      const _responseText = response.data;
      let result200: any = null;
      let resultData200 = _responseText;
      result200 = ProjectWiseConfigurationPaginatedResult.fromJS(resultData200);
      return Promise.resolve<ProjectWiseConfigurationPaginatedResult>(result200);

    } else if (status === 404) {
      const _responseText = response.data;
      let result404: any = null;
      let resultData404 = _responseText;
      result404 = ProblemDetails.fromJS(resultData404);
      return throwException("A server side error occurred.", status, _responseText, _headers, result404);

    } else if (status !== 200 && status !== 204) {
      const _responseText = response.data;
      return throwException("An unexpected server error occurred.", status, _responseText, _headers);
    }
    return Promise.resolve<ProjectWiseConfigurationPaginatedResult>(null as any);
  }

  listStorageTypes(cancelToken?: CancelToken): Promise<string[]> {
    let url_ = this.baseUrl + "/api/v1/settings/types";
    url_ = url_.replace(/[?&]$/, "");

    let options_: AxiosRequestConfig = {
      method: "GET",
      url: url_,
      headers: {
        "Accept": "application/json"
      },
      cancelToken
    };

    return this.instance.request(options_).catch((_error: any) => {
      if (isAxiosError(_error) && _error.response) {
        return _error.response;
      } else {
        throw _error;
      }
    }).then((_response: AxiosResponse) => {
      return this.processListStorageTypes(_response);
    });
  }

  protected processListStorageTypes(response: AxiosResponse): Promise<string[]> {
    const status = response.status;
    let _headers: any = {};
    if (response.headers && typeof response.headers === "object") {
      for (const k in response.headers) {
        if (response.headers.hasOwnProperty(k)) {
          _headers[k] = response.headers[k];
        }
      }
    }
    if (status === 200) {
      const _responseText = response.data;
      let result200: any = null;
      let resultData200 = _responseText;
      if (Array.isArray(resultData200)) {
        result200 = [] as any;
        for (let item of resultData200)
          result200!.push(item);
      } else {
        result200 = null as any;
      }
      return Promise.resolve<string[]>(result200);

    } else if (status === 400) {
      const _responseText = response.data;
      let result400: any = null;
      let resultData400 = _responseText;
      result400 = ProblemDetails.fromJS(resultData400);
      return throwException("A server side error occurred.", status, _responseText, _headers, result400);

    } else if (status !== 200 && status !== 204) {
      const _responseText = response.data;
      return throwException("An unexpected server error occurred.", status, _responseText, _headers);
    }
    return Promise.resolve<string[]>(null as any);
  }

  getUserAccounts(cancelToken?: CancelToken): Promise<UserAccount[]> {
    let url_ = this.baseUrl + "/api/v1/user/accounts";
    url_ = url_.replace(/[?&]$/, "");

    let options_: AxiosRequestConfig = {
      method: "GET",
      url: url_,
      headers: {
        "Accept": "application/json"
      },
      cancelToken
    };

    return this.instance.request(options_).catch((_error: any) => {
      if (isAxiosError(_error) && _error.response) {
        return _error.response;
      } else {
        throw _error;
      }
    }).then((_response: AxiosResponse) => {
      return this.processGetUserAccounts(_response);
    });
  }

  protected processGetUserAccounts(response: AxiosResponse): Promise<UserAccount[]> {
    const status = response.status;
    let _headers: any = {};
    if (response.headers && typeof response.headers === "object") {
      for (const k in response.headers) {
        if (response.headers.hasOwnProperty(k)) {
          _headers[k] = response.headers[k];
        }
      }
    }
    if (status === 200) {
      const _responseText = response.data;
      let result200: any = null;
      let resultData200 = _responseText;
      if (Array.isArray(resultData200)) {
        result200 = [] as any;
        for (let item of resultData200)
          result200!.push(UserAccount.fromJS(item));
      } else {
        result200 = null as any;
      }
      return Promise.resolve<UserAccount[]>(result200);

    } else if (status === 400) {
      const _responseText = response.data;
      let result400: any = null;
      let resultData400 = _responseText;
      result400 = ProblemDetails.fromJS(resultData400);
      return throwException("A server side error occurred.", status, _responseText, _headers, result400);

    } else if (status !== 200 && status !== 204) {
      const _responseText = response.data;
      return throwException("An unexpected server error occurred.", status, _responseText, _headers);
    }
    return Promise.resolve<UserAccount[]>(null as any);
  }

  getUserInfo(cancelToken?: CancelToken): Promise<UserBase> {
    let url_ = this.baseUrl + "/api/v1/user";
    url_ = url_.replace(/[?&]$/, "");

    let options_: AxiosRequestConfig = {
      method: "GET",
      url: url_,
      headers: {
        "Accept": "application/json"
      },
      cancelToken
    };

    return this.instance.request(options_).catch((_error: any) => {
      if (isAxiosError(_error) && _error.response) {
        return _error.response;
      } else {
        throw _error;
      }
    }).then((_response: AxiosResponse) => {
      return this.processGetUserInfo(_response);
    });
  }

  protected processGetUserInfo(response: AxiosResponse): Promise<UserBase> {
    const status = response.status;
    let _headers: any = {};
    if (response.headers && typeof response.headers === "object") {
      for (const k in response.headers) {
        if (response.headers.hasOwnProperty(k)) {
          _headers[k] = response.headers[k];
        }
      }
    }
    if (status === 200) {
      const _responseText = response.data;
      let result200: any = null;
      let resultData200 = _responseText;
      result200 = UserBase.fromJS(resultData200);
      return Promise.resolve<UserBase>(result200);

    } else if (status === 404) {
      const _responseText = response.data;
      let result404: any = null;
      let resultData404 = _responseText;
      result404 = ProblemDetails.fromJS(resultData404);
      return throwException("A server side error occurred.", status, _responseText, _headers, result404);

    } else if (status !== 200 && status !== 204) {
      const _responseText = response.data;
      return throwException("An unexpected server error occurred.", status, _responseText, _headers);
    }
    return Promise.resolve<UserBase>(null as any);
  }

  /**
   * Performs a webhook callback.
   * @param webhookBody (optional) The webhook body.
   * @param signature (optional) The webhook signature.
   * @return A result indicating the status code.
   */
  userWebhookCallback(webhookBody?: GDPRWebhook | null | undefined, signature?: string | undefined, cancelToken?: CancelToken): Promise<GDPRWebhookResponse> {
    let url_ = this.baseUrl + "/api/v1/user/gdprcallback";
    url_ = url_.replace(/[?&]$/, "");

    const content_ = JSON.stringify(webhookBody);

    let options_: AxiosRequestConfig = {
      data: content_,
      method: "POST",
      url: url_,
      headers: {
        "x-adsk-signature": signature !== undefined && signature !== null ? "" + signature : "",
        "Content-Type": "application/json",
        "Accept": "application/json"
      },
      cancelToken
    };

    return this.instance.request(options_).catch((_error: any) => {
      if (isAxiosError(_error) && _error.response) {
        return _error.response;
      } else {
        throw _error;
      }
    }).then((_response: AxiosResponse) => {
      return this.processUserWebhookCallback(_response);
    });
  }

  protected processUserWebhookCallback(response: AxiosResponse): Promise<GDPRWebhookResponse> {
    const status = response.status;
    let _headers: any = {};
    if (response.headers && typeof response.headers === "object") {
      for (const k in response.headers) {
        if (response.headers.hasOwnProperty(k)) {
          _headers[k] = response.headers[k];
        }
      }
    }
    if (status === 200) {
      const _responseText = response.data;
      let result200: any = null;
      let resultData200 = _responseText;
      result200 = GDPRWebhookResponse.fromJS(resultData200);
      return Promise.resolve<GDPRWebhookResponse>(result200);

    } else if (status === 400) {
      const _responseText = response.data;
      let result400: any = null;
      let resultData400 = _responseText;
      result400 = ProblemDetails.fromJS(resultData400);
      return throwException("A server side error occurred.", status, _responseText, _headers, result400);

    } else if (status !== 200 && status !== 204) {
      const _responseText = response.data;
      return throwException("An unexpected server error occurred.", status, _responseText, _headers);
    }
    return Promise.resolve<GDPRWebhookResponse>(null as any);
  }

  /**
   * Performs a webhook callback.
   * @param webhookBody (optional) The webhook body.
   * @param signature (optional) The webhook signature.
   * @param userId (optional) The user ID. Deprecated and will return 403 if present.
   * @return A result indicating the status code.
   */
  adskWebhookCallback(webhookBody?: WebhookBody | null | undefined, signature?: string | null | undefined, userId?: string | null | undefined, cancelToken?: CancelToken): Promise<void> {
    let url_ = this.baseUrl + "/api/v1/webhook/callback?";
    if (userId !== undefined && userId !== null)
      url_ += "userId=" + encodeURIComponent("" + userId) + "&";
    url_ = url_.replace(/[?&]$/, "");

    const content_ = JSON.stringify(webhookBody);

    let options_: AxiosRequestConfig = {
      data: content_,
      method: "POST",
      url: url_,
      headers: {
        "x-adsk-signature": signature !== undefined && signature !== null ? "" + signature : "",
        "Content-Type": "application/json",
      },
      cancelToken
    };

    return this.instance.request(options_).catch((_error: any) => {
      if (isAxiosError(_error) && _error.response) {
        return _error.response;
      } else {
        throw _error;
      }
    }).then((_response: AxiosResponse) => {
      return this.processAdskWebhookCallback(_response);
    });
  }

  protected processAdskWebhookCallback(response: AxiosResponse): Promise<void> {
    const status = response.status;
    let _headers: any = {};
    if (response.headers && typeof response.headers === "object") {
      for (const k in response.headers) {
        if (response.headers.hasOwnProperty(k)) {
          _headers[k] = response.headers[k];
        }
      }
    }
    if (status === 200) {
      const _responseText = response.data;
      return Promise.resolve<void>(null as any);

    } else if (status === 400) {
      const _responseText = response.data;
      let result400: any = null;
      let resultData400 = _responseText;
      result400 = ProblemDetails.fromJS(resultData400);
      return throwException("A server side error occurred.", status, _responseText, _headers, result400);

    } else if (status !== 200 && status !== 204) {
      const _responseText = response.data;
      return throwException("An unexpected server error occurred.", status, _responseText, _headers);
    }
    return Promise.resolve<void>(null as any);
  }
}

export class ApiException extends Error {
  override message: string;
  status: number;
  response: string;
  headers: { [key: string]: any; };
  result: any;

  constructor(message: string, status: number, response: string, headers: { [key: string]: any; }, result: any) {
    super();

    this.message = message;
    this.status = status;
    this.response = response;
    this.headers = headers;
    this.result = result;
  }

  protected isApiException = true;

  static isApiException(obj: any): obj is ApiException {
    return obj.isApiException === true;
  }
}

function throwException(message: string, status: number, response: string, headers: {
  [key: string]: any;
}, result?: any): any {
  if (result !== null && result !== undefined)
    throw result;
  else
    throw new ApiException(message, status, response, headers, null);
}

function isAxiosError(obj: any): obj is AxiosError {
  return obj && obj.isAxiosError === true;
}